import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Button,
  Alert,
  Modal,
  Form,
  Accordion,
  Badge,
  Row,
  Col,
  Pagination,
  InputGroup,
  ProgressBar,
} from 'react-bootstrap';
import {
  Quiz,
  Question,
  CreateQuestion,
  CreateAnswer,
  UpdateQuestion,
  UpdateAnswer,
  adminService,
} from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { getQuizDisplayTitle } from '../utils/quizDisplayTitle';
import { parseGeniusBulkLines, sanitizeGeniusAssetKey } from '../utils/geniusAdminBulk';
import { parseWordConnectBulkText } from '../utils/wordConnectAdminBulk';

interface QuestionManagerProps {
  quiz: Quiz;
  onBack: () => void;
  /** Atualiza o quiz no painel (ex.: após excluir todas as perguntas o status volta a Draft). */
  onQuizUpdated?: (quiz: Quiz) => void;
}

function parseCommaTags(raw: string): string[] | undefined {
  const parts = raw
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : undefined;
}

function previewField<T>(row: Record<string, unknown>, camel: string, pascal: string): T | undefined {
  const v = row[camel] ?? row[pascal];
  return v as T | undefined;
}

/** Mirrors backend ResolveMcImportCorrectFlags for per-question create on JSON import. */
function resolveMcImportCorrectFlags(
  answer: Record<string, unknown>,
  quiz: Quiz,
  excludeFromEasyDifficulty: boolean
): { isCorrectEasy: boolean; isCorrectMedium: boolean } {
  const legacy = Boolean(previewField(answer, 'isCorrect', 'IsCorrect'));
  const easyRaw = previewField<boolean>(answer, 'isCorrectEasy', 'IsCorrectEasy');
  const medRaw = previewField<boolean>(answer, 'isCorrectMedium', 'IsCorrectMedium');
  const hasEasyFlag = easyRaw !== undefined;
  const hasMedFlag = medRaw !== undefined;
  const needMed = quizNeedsMediumBand(quiz);
  const supportsEasy = quizNeedsEasy(quiz);

  let isCorrectEasy: boolean;
  if (excludeFromEasyDifficulty || !supportsEasy) {
    isCorrectEasy = hasEasyFlag && !!easyRaw;
  } else if (hasEasyFlag) {
    isCorrectEasy = !!easyRaw;
  } else {
    isCorrectEasy = legacy;
  }

  let isCorrectMedium: boolean;
  if (!needMed) {
    isCorrectMedium = false;
  } else if (hasMedFlag) {
    isCorrectMedium = !!medRaw;
  } else if (hasEasyFlag) {
    isCorrectMedium = false;
  } else {
    isCorrectMedium = legacy;
  }

  return { isCorrectEasy, isCorrectMedium };
}

function buildCreateQuestionFromPreviewItem(row: Record<string, unknown>, quiz: Quiz): CreateQuestion {
  const isWitc = quiz.format === 'Witc';
  const isGenius = quiz.format === 'Genius';
  const isImagePoolQuiz = isGenius || quiz.format === 'TileMatch';

  const text = String(previewField(row, 'text', 'Text') ?? '').trim();
  const tags = previewField<string[]>(row, 'tags', 'Tags');
  const excludeFromEasy = Boolean(
    previewField(row, 'excludeFromEasyDifficulty', 'ExcludeFromEasyDifficulty')
  );

  if (isImagePoolQuiz) {
    const imageAssetKey = previewField<string>(row, 'imageAssetKey', 'ImageAssetKey');
    const soundRaw = previewField<string>(row, 'soundAssetKey', 'SoundAssetKey');
    return {
      quizId: quiz.id,
      text: text || 'Item',
      answers: [],
      imageAssetKey: imageAssetKey ?? undefined,
      ...(isGenius && soundRaw ? { soundAssetKey: soundRaw } : {}),
      ...(tags?.length ? { tags } : {}),
    };
  }

  if (isWitc) {
    return {
      quizId: quiz.id,
      text,
      answers: [],
      textEn: previewField(row, 'textEn', 'TextEn') ?? undefined,
      imageAssetKey: previewField(row, 'imageAssetKey', 'ImageAssetKey') ?? undefined,
      correctAnswerPt: previewField(row, 'correctAnswerPt', 'CorrectAnswerPt') ?? undefined,
      correctAnswerEn: previewField(row, 'correctAnswerEn', 'CorrectAnswerEn') ?? undefined,
      correctAnswerEasyPt: previewField(row, 'correctAnswerEasyPt', 'CorrectAnswerEasyPt') ?? undefined,
      correctAnswerEasyEn: previewField(row, 'correctAnswerEasyEn', 'CorrectAnswerEasyEn') ?? undefined,
      correctAnswerMediumPt:
        previewField(row, 'correctAnswerMediumPt', 'CorrectAnswerMediumPt') ?? undefined,
      correctAnswerMediumEn:
        previewField(row, 'correctAnswerMediumEn', 'CorrectAnswerMediumEn') ?? undefined,
      excludeFromEasyDifficulty: excludeFromEasy,
      ...(tags?.length ? { tags } : {}),
    };
  }

  const answersRaw = (previewField<Record<string, unknown>[]>(row, 'answers', 'Answers') ?? []) as Record<
    string,
    unknown
  >[];
  const answers = answersRaw.map((a) => {
    const { isCorrectEasy, isCorrectMedium } = resolveMcImportCorrectFlags(a, quiz, excludeFromEasy);
    return {
      text: String(previewField(a, 'text', 'Text') ?? '').trim(),
      textEn: String(previewField(a, 'textEn', 'TextEn') ?? '').trim(),
      ...(previewField<string>(a, 'imageAssetKey', 'ImageAssetKey')
        ? { imageAssetKey: previewField<string>(a, 'imageAssetKey', 'ImageAssetKey') }
        : {}),
      isCorrect: isCorrectEasy || isCorrectMedium,
      isCorrectEasy,
      isCorrectMedium,
    };
  });

  const qKey = previewField<string>(row, 'imageAssetKey', 'ImageAssetKey');
  return {
    quizId: quiz.id,
    text,
    textEn: String(previewField(row, 'textEn', 'TextEn') ?? '').trim(),
    answers,
    excludeFromEasyDifficulty: excludeFromEasy,
    ...(qKey ? { imageAssetKey: qKey } : {}),
    ...(tags?.length ? { tags } : {}),
  };
}

function buildUpdateForUnlockLink(
  created: Question,
  prerequisiteId: string,
  unlockCount: number
): UpdateQuestion {
  if (!created.answers?.length) {
    return {
      text: created.text,
      answers: [],
      textEn: created.textEn ?? undefined,
      imageAssetKey: created.imageAssetKey ?? null,
      correctAnswerPt: created.correctAnswerPt ?? undefined,
      correctAnswerEn: created.correctAnswerEn ?? undefined,
      correctAnswerEasyPt: created.correctAnswerEasyPt ?? undefined,
      correctAnswerEasyEn: created.correctAnswerEasyEn ?? undefined,
      correctAnswerMediumPt: created.correctAnswerMediumPt ?? undefined,
      correctAnswerMediumEn: created.correctAnswerMediumEn ?? undefined,
      excludeFromEasyDifficulty: created.excludeFromEasyDifficulty ?? false,
      unlockPrerequisiteQuestionId: prerequisiteId,
      unlockPrerequisiteCorrectCount: unlockCount,
      tags: created.tags ?? [],
    };
  }

  return {
    text: created.text,
    textEn: created.textEn ?? '',
    answers: created.answers.map((a) => ({
      id: a.id,
      text: a.text,
      textEn: a.textEn ?? '',
      imageAssetKey: a.imageAssetKey ?? null,
      isCorrect: a.isCorrect,
      isCorrectEasy: a.isCorrectEasy ?? false,
      isCorrectMedium: a.isCorrectMedium ?? false,
    })),
    imageAssetKey: created.imageAssetKey ?? null,
    excludeFromEasyDifficulty: created.excludeFromEasyDifficulty ?? false,
    unlockPrerequisiteQuestionId: prerequisiteId,
    unlockPrerequisiteCorrectCount: unlockCount,
    tags: created.tags ?? [],
  };
}

function countUnlockLinksInPreview(preview: Record<string, unknown>[]): number {
  return preview.filter((row) => {
    const idx = previewField<number>(row, 'unlockPrerequisiteQuestionIndex', 'UnlockPrerequisiteQuestionIndex');
    return idx != null && idx >= 0;
  }).length;
}

const QUESTION_MANAGER_PAGE_SIZE = 50;

function truncateText(s: string, max: number): string {
  const t = s.trim();
  if (!t) return '';
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

function quizNeedsEasy(quiz: Quiz): boolean {
  return quiz.supportsPlayDifficultyEasy !== false;
}

function quizNeedsMediumBand(quiz: Quiz): boolean {
  return (
    quiz.supportsPlayDifficultyMedium !== false || quiz.supportsPlayDifficultyHard !== false
  );
}

/** Pergunta só para médio/difícil só faz sentido quando o quiz tem fácil e também médio ou difícil. */
function quizAllowsMediumOnlyQuestions(quiz: Quiz): boolean {
  return quizNeedsEasy(quiz) && quizNeedsMediumBand(quiz);
}

/** Resposta correta WITC — trechos por modo activado no quiz. */
function witcAnswerOneLine(q: Question, quiz: Quiz): string {
  const parts: string[] = [];
  const pairLine = (pt: string, en: string) => {
    if (!pt && !en) return '—';
    if (pt && en && pt.toLowerCase() !== en.toLowerCase()) return `${pt} (${en})`;
    return pt || en;
  };
  if (quizNeedsEasy(quiz)) {
    const pt = q.correctAnswerEasyPt?.trim() ?? q.correctAnswerPt?.trim() ?? '';
    const en = q.correctAnswerEasyEn?.trim() ?? q.correctAnswerEn?.trim() ?? '';
    parts.push(`fácil: ${pairLine(pt, en)}`);
  }
  if (quizNeedsMediumBand(quiz)) {
    const pt = q.correctAnswerMediumPt?.trim() ?? '';
    const en = q.correctAnswerMediumEn?.trim() ?? '';
    parts.push(`médio: ${pairLine(pt, en)}`);
  }
  return parts.join(' · ') || '—';
}

function stopAnswerOneLine(q: Question): string {
  const names = (q.answers || []).map((a) => a.text?.trim()).filter(Boolean);
  if (names.length === 0) return '—';
  const preview = names.slice(0, 3).join(', ');
  return names.length > 3 ? `${preview}… (+${names.length - 3})` : preview;
}

function mcCorrectAnswerOneLine(q: Question, quiz: Quiz): string {
  const bits: string[] = [];
  if (quizNeedsEasy(quiz)) {
    const a = q.answers.find((x) => x.isCorrectEasy ?? x.isCorrect);
    bits.push(`fácil: ${a?.text?.trim() || '—'}`);
  }
  if (quizNeedsMediumBand(quiz)) {
    const a =
      q.answers.find((x) => x.isCorrectMedium) ?? q.answers.find((x) => x.isCorrect);
    bits.push(`médio: ${a?.text?.trim() || '—'}`);
  }
  return bits.join(' · ') || '—';
}

/** Label do select de pré-requisito: texto da pergunta — resposta(s) correta(s), como na lista. */
function prerequisiteDropdownLabel(
  q: Question,
  quiz: Quiz,
  isWitc: boolean,
  isStop: boolean
): string {
  const qPart = isStop
    ? `Letra ${(q.text || '').trim() || '?'}`
    : (q.text || '').trim() || '(sem texto)';
  const ansPart = isStop
    ? stopAnswerOneLine(q)
    : isWitc
      ? witcAnswerOneLine(q, quiz)
      : mcCorrectAnswerOneLine(q, quiz);
  const combined = `${qPart} — ${ansPart}`;
  return combined.length > 200 ? `${combined.slice(0, 199)}…` : combined;
}

/** Linha «pergunta — respostas» para pré-requisito na lista (usa o mesmo formato do select). */
function unlockPrerequisiteDisplayLabel(
  row: Question,
  quiz: Quiz,
  isWitc: boolean,
  isStop: boolean,
  unlockById: Map<string, Question>,
  missingLabel: string
): string {
  const id = row.unlockPrerequisiteQuestionId;
  if (!id) return '';
  const pq = unlockById.get(id);
  if (pq) return prerequisiteDropdownLabel(pq, quiz, isWitc, isStop);
  const api = row.unlockPrerequisiteQuestionPreviewText?.trim();
  return api || missingLabel;
}

/** Texto normalizado para filtrar pré-requisitos (pergunta + respostas corretas PT/EN). */
function unlockPrerequisiteSearchHaystack(
  q: Question,
  quiz: Quiz,
  isWitc: boolean,
  isStop: boolean
): string {
  const parts: string[] = [
    q.text || '',
    q.textEn || '',
  ];
  if (isStop) {
    for (const a of q.answers || []) {
      parts.push(a.text, a.textEn || '');
    }
    parts.push(stopAnswerOneLine(q));
  } else if (isWitc) {
    parts.push(
      q.correctAnswerEasyPt || '',
      q.correctAnswerEasyEn || '',
      q.correctAnswerMediumPt || '',
      q.correctAnswerMediumEn || '',
      q.correctAnswerPt || '',
      q.correctAnswerEn || '',
      witcAnswerOneLine(q, quiz)
    );
  } else {
    for (const a of q.answers || []) {
      parts.push(a.text, a.textEn || '');
    }
    parts.push(mcCorrectAnswerOneLine(q, quiz));
  }
  return parts.join(' ').toLowerCase();
}

const QuestionManager: React.FC<QuestionManagerProps> = ({ quiz, onBack, onQuizUpdated }) => {
  const { i18n, t } = useSafeTranslation();
  const isWitc = quiz.format === 'Witc';
  const isStop = quiz.format === 'Stop';
  const isGenius = quiz.format === 'Genius';
  const isTileMatch = quiz.format === 'TileMatch';
  const isWordConnect = quiz.format === 'WordConnect';
  const isImagePoolQuiz = isGenius || isTileMatch;
  const isWordPoolQuiz = isWordConnect;
  const STOP_MIN_ANSWERS = 3;

  type StopFormAnswerRow = {
    id?: string;
    text: string;
    textEn: string;
    imageAssetKey: string;
    isCorrect: boolean;
    isCorrectEasy: boolean;
    isCorrectMedium: boolean;
  };

  const emptyStopAnswerRow = (): StopFormAnswerRow => ({
    text: '',
    textEn: '',
    imageAssetKey: '',
    isCorrect: true,
    isCorrectEasy: false,
    isCorrectMedium: false,
  });

  const mapStopAnswersForCreate = (): CreateAnswer[] =>
    formData.answers
      .filter((a) => a.text.trim() && a.textEn.trim() && a.imageAssetKey.trim())
      .map((a) => ({
        text: a.text.trim(),
        textEn: a.textEn.trim(),
        imageAssetKey: a.imageAssetKey.replace(/[^a-zA-Z0-9_-]/g, ''),
        isCorrect: true,
        isCorrectEasy: false,
        isCorrectMedium: true,
      }));

  /** Stop replaces all answers server-side; id is only for TS (existing rows keep id, new rows get a placeholder). */
  const mapStopAnswersForUpdate = (): UpdateAnswer[] =>
    formData.answers
      .filter((a) => a.text.trim() && a.textEn.trim() && a.imageAssetKey.trim())
      .map((a) => ({
        id: a.id ?? crypto.randomUUID(),
        text: a.text.trim(),
        textEn: a.textEn.trim(),
        imageAssetKey: a.imageAssetKey.replace(/[^a-zA-Z0-9_-]/g, ''),
        isCorrect: true,
        isCorrectEasy: false,
        isCorrectMedium: true,
      }));
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [isPurgingAllQuestions, setIsPurgingAllQuestions] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [importJson, setImportJson] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{
    current: number;
    total: number;
    phase: 'creating' | 'unlock' | '';
  }>({ current: 0, total: 0, phase: '' });
  const [geniusBulkText, setGeniusBulkText] = useState('');
  const [wordConnectBulkText, setWordConnectBulkText] = useState('');
  const [geniusBulkSaving, setGeniusBulkSaving] = useState(false);
  const [formData, setFormData] = useState<{
    text: string;
    /** MC: English question line (required with Portuguese text). */
    textEn: string;
    tags: string;
    /** Multiple-choice optional image basename (requires quiz mc folder). */
    imageAssetKey: string;
    soundAssetKey: string;
    answers: Array<{
      id?: string;
      text: string;
      textEn: string;
      imageAssetKey: string;
      isCorrect: boolean;
      isCorrectEasy: boolean;
      isCorrectMedium: boolean;
    }>;
    excludeFromEasyDifficulty: boolean;
    unlockPrerequisiteQuestionId: string;
    unlockPrerequisiteCorrectCount: number;
  }>({
    text: '',
    textEn: '',
    tags: '',
    imageAssetKey: '',
    soundAssetKey: '',
    excludeFromEasyDifficulty: false,
    unlockPrerequisiteQuestionId: '',
    unlockPrerequisiteCorrectCount: 1,
    answers: [
      { text: '', textEn: '', imageAssetKey: '', isCorrect: false, isCorrectEasy: false, isCorrectMedium: false },
      { text: '', textEn: '', imageAssetKey: '', isCorrect: false, isCorrectEasy: false, isCorrectMedium: false },
      { text: '', textEn: '', imageAssetKey: '', isCorrect: false, isCorrectEasy: false, isCorrectMedium: false },
      { text: '', textEn: '', imageAssetKey: '', isCorrect: false, isCorrectEasy: false, isCorrectMedium: false },
      { text: '', textEn: '', imageAssetKey: '', isCorrect: false, isCorrectEasy: false, isCorrectMedium: false }
    ]
  });

  const [witcFields, setWitcFields] = useState({
    textEn: '',
    imageAssetKey: '',
    correctAnswerEasyPt: '',
    correctAnswerEasyEn: '',
    correctAnswerMediumPt: '',
    correctAnswerMediumEn: '',
    excludeFromEasyDifficulty: false,
  });

  /** Text in the search box (does not hit the API until applied). */
  const [listSearchInput, setListSearchInput] = useState('');
  /** Term sent to the backend (set only on Enter or search button). */
  const [listSearchApplied, setListSearchApplied] = useState('');
  const [listPage, setListPage] = useState(1);
  const [listTotalCount, setListTotalCount] = useState(0);
  const [listTotalPages, setListTotalPages] = useState(1);
  const [listHasNext, setListHasNext] = useState(false);
  const [listHasPrev, setListHasPrev] = useState(false);

  /** Todas as perguntas do quiz (sem filtro de lista): select de pré-requisito + rótulos na listagem. */
  const [unlockCatalog, setUnlockCatalog] = useState<Question[]>([]);
  /** Filtro local do select de pré-requisito (pergunta ou resposta correta). */
  const [unlockPickerSearch, setUnlockPickerSearch] = useState('');

  const unlockById = useMemo(() => {
    const m = new Map<string, Question>();
    for (const q of unlockCatalog) {
      m.set(q.id, q);
    }
    return m;
  }, [unlockCatalog]);

  useEffect(() => {
    if (showCreateModal || showEditModal) {
      setUnlockPickerSearch('');
    }
  }, [showCreateModal, showEditModal]);

  const loadUnlockCatalog = useCallback(async () => {
    if (!quiz.characterUnlockEnabled) {
      setUnlockCatalog([]);
      return;
    }
    try {
      const all: Question[] = [];
      let page = 1;
      let more = true;
      while (more) {
        const res = await adminService.getQuizQuestions(quiz.id, { page, pageSize: 200 });
        all.push(...res.questions);
        more = res.hasNextPage;
        page++;
      }
      setUnlockCatalog(all);
    } catch {
      setUnlockCatalog([]);
    }
  }, [quiz.id, quiz.characterUnlockEnabled]);

  const loadQuestions = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await adminService.getQuizQuestions(quiz.id, {
        page: listPage,
        pageSize: QUESTION_MANAGER_PAGE_SIZE,
        search: listSearchApplied.trim() || undefined,
      });
      setQuestions(res.questions);
      setListTotalCount(res.totalCount);
      setListTotalPages(res.totalCount === 0 ? 1 : Math.max(1, res.totalPages));
      setListHasNext(res.hasNextPage);
      setListHasPrev(res.hasPreviousPage);
      setError(null);
    } catch {
      setError('Failed to load questions');
    } finally {
      setIsLoading(false);
    }
  }, [quiz.id, listPage, listSearchApplied]);

  useEffect(() => {
    void loadUnlockCatalog();
  }, [loadUnlockCatalog]);

  const applyListSearch = useCallback(() => {
    const q = listSearchInput.trim();
    setListSearchApplied(q);
    setListPage(1);
  }, [listSearchInput]);

  useEffect(() => {
    setListSearchInput('');
    setListSearchApplied('');
    setListPage(1);
  }, [quiz.id]);

  useEffect(() => {
    void loadQuestions();
  }, [loadQuestions]);

  useEffect(() => {
    if (listTotalPages > 0 && listPage > listTotalPages) {
      setListPage(listTotalPages);
    }
  }, [listTotalPages, listPage]);

  const totalFiltered = listTotalCount;
  const totalPages = listTotalPages;

  const resetForm = () => {
    setFormData({
      text: '',
      textEn: '',
      tags: '',
      imageAssetKey: '',
      soundAssetKey: '',
      excludeFromEasyDifficulty: false,
      unlockPrerequisiteQuestionId: '',
      unlockPrerequisiteCorrectCount: 1,
      answers: isStop
        ? [emptyStopAnswerRow(), emptyStopAnswerRow(), emptyStopAnswerRow()]
        : [
            { text: '', textEn: '', imageAssetKey: '', isCorrect: false, isCorrectEasy: false, isCorrectMedium: false },
            { text: '', textEn: '', imageAssetKey: '', isCorrect: false, isCorrectEasy: false, isCorrectMedium: false },
            { text: '', textEn: '', imageAssetKey: '', isCorrect: false, isCorrectEasy: false, isCorrectMedium: false },
            { text: '', textEn: '', imageAssetKey: '', isCorrect: false, isCorrectEasy: false, isCorrectMedium: false },
            { text: '', textEn: '', imageAssetKey: '', isCorrect: false, isCorrectEasy: false, isCorrectMedium: false },
          ],
    });
    setWitcFields({
      textEn: '',
      imageAssetKey: '',
      correctAnswerEasyPt: '',
      correctAnswerEasyEn: '',
      correctAnswerMediumPt: '',
      correctAnswerMediumEn: '',
      excludeFromEasyDifficulty: false,
    });
    setGeniusBulkText('');
    setWordConnectBulkText('');
  };

  const geniusBulkPreview = useMemo(() => parseGeniusBulkLines(geniusBulkText), [geniusBulkText]);

  const imagePoolBasePath = isTileMatch
    ? quiz.tileMatchImageAssetsBasePath
    : quiz.geniusImageAssetsBasePath;

  const renderImagePoolBulkCreateFields = () => (
    <>
      <Alert variant="info" className="small">
        {isTileMatch
          ? t(
              'admin.tileMatchPoolHelp',
              'Cadastre apenas imagens (ImageAssetKey). Não há respostas corretas: na partida o servidor sorteia peças do pool e monta 10 rodadas automaticamente.'
            )
          : t(
              'admin.geniusPoolHelp',
              'Cadastre apenas pares imagem + som. Não há resposta correta: na partida o servidor sorteia 4 itens do pool e monta as 10 rodadas automaticamente.'
            )}
      </Alert>
      <p className="small text-muted mb-2">
        {t('admin.geniusAssetPaths', 'Arquivos esperados:')}{' '}
        <code>public/assets/images/{imagePoolBasePath || '…'}/{'{chave}'}.webp</code>
        {isGenius ? (
          <>
            {' · '}
            <code>public/assets/sounds/{quiz.geniusSoundAssetsBasePath || '…'}/{'{chave}'}.mp3</code>{' '}
            {t('admin.geniusOr', 'ou')} <code>.wav</code>
          </>
        ) : (
          <>
            {' '}
            {t('admin.geniusOr', 'ou')} <code>.png</code>
          </>
        )}
      </p>
      <Form.Group className="mb-3">
        <Form.Label>
          {t('admin.geniusBulkLabel', 'Itens do pool (um por linha) *')}
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={10}
          value={geniusBulkText}
          onChange={(e) => setGeniusBulkText(e.target.value)}
          placeholder={
            isTileMatch ? `tile_01\ntile_02\ntile_03,Label opcional` : `001\n002\n003,003\n004,004-sfx,Pikachu`
          }
          spellCheck={false}
        />
        <Form.Text className="text-muted d-block mt-2">
          {isTileMatch
            ? t(
                'admin.tileMatchBulkFormat',
                'Uma chave por linha (ImageAssetKey). Opcional: chave,rótulo. Linhas duplicadas e vazias são ignoradas.'
              )
            : t(
                'admin.geniusBulkFormat',
                'Formato: chave da imagem; ou imagem,som; ou imagem,som,rótulo opcional. Se o som for omitido, usa a mesma chave da imagem. Linhas duplicadas e linhas vazias são ignoradas.'
              )}
        </Form.Text>
      </Form.Group>
      {geniusBulkPreview.length > 0 ? (
        <p className="small mb-0">
          <strong>{geniusBulkPreview.length}</strong>{' '}
          {t('admin.geniusBulkPreviewCount', 'item(ns) serão criados')}:{' '}
          {geniusBulkPreview
            .slice(0, 8)
            .map((i) => (isGenius ? `${i.imageAssetKey}→${i.soundAssetKey}` : i.imageAssetKey))
            .join(', ')}
          {geniusBulkPreview.length > 8 ? '…' : ''}
        </p>
      ) : null}
    </>
  );

  const renderImagePoolEditFields = () => (
    <>
      <Form.Group className="mb-3">
        <Form.Label>{t('admin.geniusItemLabel', 'Rótulo (opcional)')}</Form.Label>
        <Form.Control
          type="text"
          value={formData.text}
          onChange={(e) => setFormData({ ...formData, text: e.target.value })}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>ImageAssetKey *</Form.Label>
        <Form.Control
          type="text"
          value={formData.imageAssetKey}
          onChange={(e) =>
            setFormData({
              ...formData,
              imageAssetKey: e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''),
            })
          }
          required
        />
        <Form.Text className="text-muted">
          <code>public/assets/images/{imagePoolBasePath || '…'}/</code>
        </Form.Text>
      </Form.Group>
      {isGenius ? (
        <Form.Group className="mb-3">
          <Form.Label>SoundAssetKey</Form.Label>
          <Form.Control
            type="text"
            value={formData.soundAssetKey}
            onChange={(e) =>
              setFormData({
                ...formData,
                soundAssetKey: e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''),
              })
            }
            placeholder={t('admin.geniusSoundPlaceholder', 'igual à imagem se vazio')}
          />
          <Form.Text className="text-muted">
            <code>public/assets/sounds/{quiz.geniusSoundAssetsBasePath || '…'}/</code>
          </Form.Text>
        </Form.Group>
      ) : null}
    </>
  );

  const buildUnlockPayload = () => {
    if (!quiz.characterUnlockEnabled) {
      return {
        unlockPrerequisiteQuestionId: null as string | null,
        unlockPrerequisiteCorrectCount: 1,
      };
    }
    const id = formData.unlockPrerequisiteQuestionId.trim();
    return {
      unlockPrerequisiteQuestionId: (id || null) as string | null,
      unlockPrerequisiteCorrectCount: Math.max(1, formData.unlockPrerequisiteCorrectCount || 1),
    };
  };

  const renderStopQuestionFields = () => (
    <>
      <Row className="mb-3 g-2">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Letra (português) *</Form.Label>
            <Form.Control
              type="text"
              maxLength={1}
              value={formData.text}
              onChange={(e) =>
                setFormData({ ...formData, text: e.target.value.slice(0, 1) })
              }
              placeholder="A"
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Letter (English) *</Form.Label>
            <Form.Control
              type="text"
              maxLength={1}
              value={formData.textEn}
              onChange={(e) =>
                setFormData({ ...formData, textEn: e.target.value.slice(0, 1) })
              }
              placeholder="A"
              required
            />
          </Form.Group>
        </Col>
      </Row>
      <Form.Label>Respostas aceites * (mín. {STOP_MIN_ANSWERS}; PT, EN e imagem cada)</Form.Label>
      {formData.answers.map((answer, index) => (
        <Form.Group key={index} className="mb-3 border rounded p-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="small fw-semibold text-muted">Resposta {index + 1}</span>
            {formData.answers.length > STOP_MIN_ANSWERS && (
              <Button
                type="button"
                variant="outline-danger"
                size="sm"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    answers: prev.answers.filter((_, i) => i !== index),
                  }))
                }
              >
                Remover
              </Button>
            )}
          </div>
          <Row className="g-2">
            <Col md={4}>
              <Form.Label className="small text-muted mb-1">Nome (PT)</Form.Label>
              <Form.Control
                type="text"
                value={answer.text}
                onChange={(e) => handleAnswerChange(index, 'text', e.target.value)}
                required
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small text-muted mb-1">Name (EN)</Form.Label>
              <Form.Control
                type="text"
                value={answer.textEn}
                onChange={(e) => handleAnswerChange(index, 'textEn', e.target.value)}
                required
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small text-muted mb-1">Imagem *</Form.Label>
              <Form.Control
                type="text"
                value={answer.imageAssetKey}
                onChange={(e) =>
                  handleAnswerChange(
                    index,
                    'imageAssetKey',
                    e.target.value.replace(/[^a-zA-Z0-9_-]/g, '')
                  )
                }
                placeholder="chave sem extensão"
                required
              />
            </Col>
          </Row>
        </Form.Group>
      ))}
      <Button
        type="button"
        variant="outline-secondary"
        size="sm"
        className="mb-3"
        onClick={() =>
          setFormData((prev) => ({
            ...prev,
            answers: [...prev.answers, emptyStopAnswerRow()],
          }))
        }
      >
        + Adicionar resposta
      </Button>
      <Form.Text className="text-muted d-block mb-3">
        Imagens em{' '}
        <code>public/assets/images/{quiz.witcImageAssetsBasePath?.trim() || '…'}/</code>
      </Form.Text>
    </>
  );

  const renderCharacterUnlockSection = () => {
    if (!quiz.characterUnlockEnabled) return null;
    const otherQuestions = unlockCatalog.filter(
      (q) => !selectedQuestion || q.id !== selectedQuestion.id
    );
    const needle = unlockPickerSearch.trim().toLowerCase();
    let filteredOther = needle
      ? otherQuestions.filter((q) =>
          unlockPrerequisiteSearchHaystack(q, quiz, isWitc, isStop).includes(needle)
        )
      : otherQuestions;
    const selectedPrereqId = formData.unlockPrerequisiteQuestionId;
    if (selectedPrereqId) {
      const selectedRow = otherQuestions.find((q) => q.id === selectedPrereqId);
      if (
        selectedRow &&
        !filteredOther.some((q) => q.id === selectedPrereqId)
      ) {
        filteredOther = [selectedRow, ...filteredOther];
      }
    }

    return (
      <div className="border rounded p-3 mb-3 bg-light">
        <div className="fw-semibold mb-2">Desbloqueio de personagem</div>
        <Form.Group className="mb-2">
          <Form.Label className="small mb-1">Pergunta pré-requisito (mesmo quiz)</Form.Label>
          <InputGroup className="mb-2">
            <InputGroup.Text aria-hidden>
              <i className="bi bi-search" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              value={unlockPickerSearch}
              onChange={(e) => setUnlockPickerSearch(e.target.value)}
              placeholder="Filtrar por texto da pergunta ou da resposta correta…"
              autoComplete="off"
              aria-label="Filtrar lista de perguntas pré-requisito"
            />
          </InputGroup>
          <Form.Select
            value={formData.unlockPrerequisiteQuestionId}
            onChange={(e) =>
              setFormData({ ...formData, unlockPrerequisiteQuestionId: e.target.value })
            }
          >
            <option value="">Nenhuma</option>
            {filteredOther.map((q) => (
              <option key={q.id} value={q.id}>
                {prerequisiteDropdownLabel(q, quiz, isWitc, isStop)}
              </option>
            ))}
          </Form.Select>
          {needle ? (
            <Form.Text className="text-muted d-block mt-1">
              {filteredOther.length === 0
                ? 'Nenhuma pergunta corresponde ao filtro.'
                : `Mostrando ${filteredOther.length} de ${otherQuestions.length} pergunta(s).`}
            </Form.Text>
          ) : null}
          <Form.Text className="text-muted d-block">
            O jogador precisa acertar o pré-requisito o número de vezes indicado abaixo para esta
            pergunta poder entrar no sorteio (respeitando também fácil/médio/difícil).
          </Form.Text>
        </Form.Group>
        <Form.Group className="mb-0">
          <Form.Label className="small mb-1">Vezes que precisa acertar o pré-requisito</Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={formData.unlockPrerequisiteCorrectCount}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              setFormData({
                ...formData,
                unlockPrerequisiteCorrectCount: Number.isFinite(n) ? Math.max(1, n) : 1,
              });
            }}
          />
        </Form.Group>
      </div>
    );
  };

  const handleCreateQuestion = async () => {
    try {
      const extraTags = parseCommaTags(formData.tags);
      const unlock = buildUnlockPayload();
      if (isWordPoolQuiz) {
        const words = parseWordConnectBulkText(wordConnectBulkText);
        if (words.length === 0) {
          setError(
            t('admin.wordConnectBulkEmpty', 'Informe ao menos uma palavra válida (2+ letras).')
          );
          return;
        }
        setGeniusBulkSaving(true);
        try {
          for (const word of words) {
            await adminService.createQuestion({
              quizId: quiz.id,
              text: word,
              textEn: word,
              answers: [],
            });
          }
        } finally {
          setGeniusBulkSaving(false);
        }
      } else if (isImagePoolQuiz) {
        const items = parseGeniusBulkLines(geniusBulkText);
        if (items.length === 0) {
          setError(
            t('admin.geniusBulkEmpty', 'Informe ao menos uma linha válida (ex.: 001 ou 001,001).')
          );
          return;
        }
        setGeniusBulkSaving(true);
        try {
          for (const item of items) {
            await adminService.createQuestion({
              quizId: quiz.id,
              text: item.label?.trim() || 'Item',
              answers: [],
              imageAssetKey: item.imageAssetKey,
              ...(isGenius ? { soundAssetKey: item.soundAssetKey } : {}),
            });
          }
        } finally {
          setGeniusBulkSaving(false);
        }
      } else if (isStop) {
        await adminService.createQuestion({
          quizId: quiz.id,
          text: formData.text.trim(),
          textEn: formData.textEn.trim(),
          answers: mapStopAnswersForCreate(),
          ...unlock,
          ...(extraTags ? { tags: extraTags } : {}),
        });
      } else if (isWitc) {
        const ex = quizAllowsMediumOnlyQuestions(quiz) && witcFields.excludeFromEasyDifficulty;
        await adminService.createQuestion({
          quizId: quiz.id,
          text: formData.text,
          answers: [],
          textEn: witcFields.textEn || undefined,
          imageAssetKey: witcFields.imageAssetKey.replace(/[^a-zA-Z0-9_-]/g, ''),
          correctAnswerEasyPt: quizNeedsEasy(quiz) && !ex ? witcFields.correctAnswerEasyPt : undefined,
          correctAnswerEasyEn: quizNeedsEasy(quiz) && !ex ? witcFields.correctAnswerEasyEn : undefined,
          correctAnswerMediumPt: quizNeedsMediumBand(quiz) ? witcFields.correctAnswerMediumPt : undefined,
          correctAnswerMediumEn: quizNeedsMediumBand(quiz) ? witcFields.correctAnswerMediumEn : undefined,
          excludeFromEasyDifficulty: ex,
          ...unlock,
          ...(extraTags ? { tags: extraTags } : {}),
        });
      } else {
        const key = formData.imageAssetKey.replace(/[^a-zA-Z0-9_-]/g, '');
        const exMc = quizAllowsMediumOnlyQuestions(quiz) && formData.excludeFromEasyDifficulty;
        await adminService.createQuestion({
          quizId: quiz.id,
          text: formData.text,
          textEn: formData.textEn.trim(),
          answers: formData.answers.map(({ text, textEn, imageAssetKey, isCorrectEasy, isCorrectMedium }) => {
            const imgKey = imageAssetKey.replace(/[^a-zA-Z0-9_-]/g, '');
            return {
              text,
              textEn: textEn.trim(),
              ...(imgKey ? { imageAssetKey: imgKey } : {}),
              isCorrect: isCorrectEasy || isCorrectMedium,
              isCorrectEasy,
              isCorrectMedium,
            };
          }),
          excludeFromEasyDifficulty: exMc,
          ...unlock,
          ...(key ? { imageAssetKey: key } : {}),
          ...(extraTags ? { tags: extraTags } : {}),
        });
      }
      setShowCreateModal(false);
      resetForm();
      await loadQuestions();
      void loadUnlockCatalog();
      setError(null);
    } catch {
      setError('Failed to create question');
    }
  };

  const handleEditQuestion = async () => {
    if (!selectedQuestion) return;
    
    try {
      const unlock = buildUnlockPayload();
      if (isImagePoolQuiz) {
        const img = formData.imageAssetKey.replace(/[^a-zA-Z0-9_-]/g, '');
        await adminService.updateQuestion(selectedQuestion.id, {
          text: formData.text.trim() || 'Item',
          answers: [],
          imageAssetKey: img,
          ...(isGenius
            ? {
                soundAssetKey: (formData.soundAssetKey || formData.imageAssetKey).replace(
                  /[^a-zA-Z0-9_-]/g,
                  ''
                ),
              }
            : {}),
          tags: parseCommaTags(formData.tags) ?? [],
        });
      } else if (isStop) {
        await adminService.updateQuestion(selectedQuestion.id, {
          text: formData.text.trim(),
          textEn: formData.textEn.trim(),
          answers: mapStopAnswersForUpdate(),
          ...unlock,
          tags: parseCommaTags(formData.tags) ?? [],
        });
      } else if (isWordPoolQuiz) {
        await adminService.updateQuestion(selectedQuestion.id, {
          text: formData.text.trim().toUpperCase().replace(/[^A-ZÁÀÂÃÉÊÍÓÔÕÚÜÇ]/g, ''),
          textEn: formData.textEn.trim().toUpperCase().replace(/[^A-ZÁÀÂÃÉÊÍÓÔÕÚÜÇ]/g, ''),
          answers: [],
        });
      } else if (isWitc) {
        const ex = quizAllowsMediumOnlyQuestions(quiz) && witcFields.excludeFromEasyDifficulty;
        await adminService.updateQuestion(selectedQuestion.id, {
          text: formData.text,
          answers: [],
          textEn: witcFields.textEn || undefined,
          imageAssetKey: witcFields.imageAssetKey.replace(/[^a-zA-Z0-9_-]/g, ''),
          correctAnswerEasyPt: quizNeedsEasy(quiz) && !ex ? witcFields.correctAnswerEasyPt : undefined,
          correctAnswerEasyEn: quizNeedsEasy(quiz) && !ex ? witcFields.correctAnswerEasyEn : undefined,
          correctAnswerMediumPt: quizNeedsMediumBand(quiz) ? witcFields.correctAnswerMediumPt : undefined,
          correctAnswerMediumEn: quizNeedsMediumBand(quiz) ? witcFields.correctAnswerMediumEn : undefined,
          excludeFromEasyDifficulty: ex,
          ...unlock,
          tags: parseCommaTags(formData.tags) ?? [],
        });
      } else {
        const key = formData.imageAssetKey.replace(/[^a-zA-Z0-9_-]/g, '');
        const exMc = quizAllowsMediumOnlyQuestions(quiz) && formData.excludeFromEasyDifficulty;
        await adminService.updateQuestion(selectedQuestion.id, {
          text: formData.text,
          textEn: formData.textEn.trim(),
          answers: formData.answers
            .filter((a) => a.id)
            .map((a) => {
              const imgKey = a.imageAssetKey.replace(/[^a-zA-Z0-9_-]/g, '');
              return {
                id: a.id!,
                text: a.text,
                textEn: a.textEn.trim(),
                imageAssetKey: imgKey || null,
                isCorrect: a.isCorrectEasy || a.isCorrectMedium,
                isCorrectEasy: a.isCorrectEasy,
                isCorrectMedium: a.isCorrectMedium,
              };
            }),
          imageAssetKey: key || null,
          excludeFromEasyDifficulty: exMc,
          ...unlock,
          tags: parseCommaTags(formData.tags) ?? [],
        });
      }
      setShowEditModal(false);
      setSelectedQuestion(null);
      resetForm();
      await loadQuestions();
      void loadUnlockCatalog();
      setError(null);
    } catch {
      setError('Failed to update question');
    }
  };

  const handleDeleteQuestion = async () => {
    if (!selectedQuestion) return;
    
    try {
      await adminService.deleteQuestion(selectedQuestion.id);
      setShowDeleteModal(false);
      setSelectedQuestion(null);
      await loadQuestions();
      void loadUnlockCatalog();
      setError(null);
    } catch {
      setError('Failed to delete question');
    }
  };

  const handleDeleteAllQuestions = async () => {
    setIsPurgingAllQuestions(true);
    try {
      await adminService.deleteAllQuizQuestions(quiz.id);
      const updated = await adminService.getQuizById(quiz.id);
      onQuizUpdated?.(updated);
      setShowDeleteAllModal(false);
      await loadQuestions();
      void loadUnlockCatalog();
      setError(null);
    } catch {
      setError(
        t(
          'admin.deleteAllQuestionsError',
          'Failed to delete all questions. Please try again.'
        )
      );
    } finally {
      setIsPurgingAllQuestions(false);
    }
  };

  const handleEditClick = (question: Question) => {
    setSelectedQuestion(question);
    const mappedAnswers: StopFormAnswerRow[] = question.answers.map((a) => ({
      id: a.id,
      text: a.text,
      textEn: a.textEn?.trim() ?? '',
      imageAssetKey: a.imageAssetKey || '',
      isCorrect: a.isCorrect,
      isCorrectEasy: a.isCorrectEasy ?? a.isCorrect,
      isCorrectMedium: a.isCorrectMedium ?? a.isCorrect,
    }));
    while (isStop && mappedAnswers.length < STOP_MIN_ANSWERS) {
      mappedAnswers.push(emptyStopAnswerRow());
    }
    setFormData({
      text: question.text,
      textEn: question.textEn?.trim() ?? '',
      tags: question.tags?.length ? question.tags.join(', ') : '',
      imageAssetKey: question.imageAssetKey || '',
      soundAssetKey: question.soundAssetKey || question.imageAssetKey || '',
      excludeFromEasyDifficulty: question.excludeFromEasyDifficulty === true,
      unlockPrerequisiteQuestionId: question.unlockPrerequisiteQuestionId ?? '',
      unlockPrerequisiteCorrectCount: Math.max(
        1,
        question.unlockPrerequisiteCorrectCount ?? 1
      ),
      answers: mappedAnswers,
    });
    setWitcFields({
      textEn: question.textEn || '',
      imageAssetKey: question.imageAssetKey || '',
      correctAnswerEasyPt:
        question.correctAnswerEasyPt ?? question.correctAnswerPt ?? '',
      correctAnswerEasyEn:
        question.correctAnswerEasyEn ?? question.correctAnswerEn ?? '',
      correctAnswerMediumPt: question.correctAnswerMediumPt ?? '',
      correctAnswerMediumEn: question.correctAnswerMediumEn ?? '',
      excludeFromEasyDifficulty: question.excludeFromEasyDifficulty === true,
    });
    setShowEditModal(true);
  };

  const handleAnswerChange = (
    index: number,
    field: 'text' | 'textEn' | 'imageAssetKey' | 'isCorrectEasy' | 'isCorrectMedium',
    value: string | boolean
  ) => {
    const newAnswers = [...formData.answers];
    if (field === 'text') {
      newAnswers[index].text = value as string;
    } else if (field === 'textEn') {
      newAnswers[index].textEn = value as string;
    } else if (field === 'imageAssetKey') {
      newAnswers[index].imageAssetKey = value as string;
    } else if (field === 'isCorrectEasy') {
      newAnswers.forEach((answer, i) => {
        answer.isCorrectEasy = i === index && value === true;
        answer.isCorrect = answer.isCorrectEasy || answer.isCorrectMedium;
      });
    } else {
      newAnswers.forEach((answer, i) => {
        answer.isCorrectMedium = i === index && value === true;
        answer.isCorrect = answer.isCorrectEasy || answer.isCorrectMedium;
      });
    }
    setFormData({ ...formData, answers: newAnswers });
  };

  const isFormValid = () => {
    if (isWordPoolQuiz) {
      if (showEditModal) {
        const normPt = formData.text.trim().toUpperCase().replace(/[^A-ZÁÀÂÃÉÊÍÓÔÕÚÜÇ]/g, '');
        const normEn = formData.textEn.trim().toUpperCase().replace(/[^A-ZÁÀÂÃÉÊÍÓÔÕÚÜÇ]/g, '');
        return normPt.length >= 2 && normEn.length >= 2;
      }
      return parseWordConnectBulkText(wordConnectBulkText).length > 0;
    }
    if (isImagePoolQuiz) {
      if (showEditModal) {
        return sanitizeGeniusAssetKey(formData.imageAssetKey).length > 0;
      }
      return parseGeniusBulkLines(geniusBulkText).length > 0;
    }
    if (isStop) {
      if (formData.text.trim().length !== 1 || formData.textEn.trim().length !== 1) return false;
      return mapStopAnswersForCreate().length >= STOP_MIN_ANSWERS;
    }
    if (isWitc) {
      const base =
        formData.text.trim() &&
        witcFields.textEn.trim() &&
        witcFields.imageAssetKey.trim();
      if (!base) return false;
      const exW = quizAllowsMediumOnlyQuestions(quiz) && witcFields.excludeFromEasyDifficulty;
      if (quizNeedsEasy(quiz) && !exW && (!witcFields.correctAnswerEasyPt.trim() || !witcFields.correctAnswerEasyEn.trim()))
        return false;
      if (
        quizNeedsMediumBand(quiz) &&
        (!witcFields.correctAnswerMediumPt.trim() || !witcFields.correctAnswerMediumEn.trim())
      )
        return false;
      return true;
    }
    const answersOk =
      formData.text.trim() &&
      formData.textEn.trim() &&
      formData.answers.every((a) => a.text.trim() && a.textEn.trim());
    if (!answersOk) return false;
    const exMc = quizAllowsMediumOnlyQuestions(quiz) && formData.excludeFromEasyDifficulty;
    if (quizNeedsEasy(quiz)) {
      const n = formData.answers.filter((a) => a.isCorrectEasy).length;
      if (exMc) {
        if (n !== 0) return false;
      } else if (n !== 1) return false;
    }
    if (quizNeedsMediumBand(quiz)) {
      const n = formData.answers.filter((a) => a.isCorrectMedium).length;
      if (n !== 1) return false;
    }
    return true;
  };

  const canActivateQuiz = () => {
    if (quiz.status !== 'Draft') return false;
    if (quiz.canActivate === true) return true;
    if (quiz.canActivate === false) return false;
    if (isWordPoolQuiz || isImagePoolQuiz) return false;
    return (quiz.totalQuestions ?? 0) >= quiz.questionCount;
  };

  const handleActivateQuiz = async () => {
    try {
      await adminService.activateQuiz(quiz.id);
      setError(null);
      onBack();
    } catch (err: unknown) {
      const apiMessage =
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        typeof (err as { response?: { data?: { message?: string } } }).response?.data?.message ===
          'string'
          ? (err as { response: { data: { message: string } } }).response.data.message
          : null;
      if (apiMessage) {
        setError(apiMessage);
        return;
      }
      try {
        const refreshed = await adminService.getQuizById(quiz.id);
        onQuizUpdated?.(refreshed);
        if (refreshed.activationBlockers?.length) {
          setError(refreshed.activationBlockers.join(' '));
        } else {
          setError(t('admin.activateQuizFailed', 'Failed to activate quiz'));
        }
      } catch {
        setError(t('admin.activateQuizFailed', 'Failed to activate quiz'));
      }
    }
  };

  const closeImportModal = () => {
    if (isImporting) return;
    setShowImportModal(false);
    setImportJson('');
    setImportError(null);
    setImportPreview([]);
  };

  const handleImportPreview = async () => {
    try {
      setImportError(null);
      const response = await adminService.validateQuestionsJson(quiz.id, importJson);
      const list = response?.validatedQuestions ?? response?.ValidatedQuestions;
      setImportPreview(Array.isArray(list) ? list : []);
    } catch (error: any) {
      setImportError(error.response?.data?.message || 'Error validating JSON');
      setImportPreview([]);
    }
  };

  const handleImportQuestions = async () => {
    if (importPreview.length === 0 || isImporting) return;

    const previewRows = importPreview as Record<string, unknown>[];
    const totalQuestions = previewRows.length;
    const unlockTotal =
      quiz.characterUnlockEnabled ? countUnlockLinksInPreview(previewRows) : 0;

    setIsImporting(true);
    setImportError(null);
    setImportProgress({ current: 0, total: totalQuestions, phase: 'creating' });

    const created: Question[] = [];

    try {
      for (let i = 0; i < previewRows.length; i++) {
        const saved = await adminService.createQuestion(
          buildCreateQuestionFromPreviewItem(previewRows[i], quiz)
        );
        created.push(saved);
        setImportProgress({ current: i + 1, total: totalQuestions, phase: 'creating' });
      }

      if (quiz.characterUnlockEnabled && unlockTotal > 0) {
        setImportProgress({ current: 0, total: unlockTotal, phase: 'unlock' });
        let unlockDone = 0;
        for (let i = 0; i < previewRows.length; i++) {
          const unlockIdx = previewField<number>(
            previewRows[i],
            'unlockPrerequisiteQuestionIndex',
            'UnlockPrerequisiteQuestionIndex'
          );
          if (unlockIdx == null || unlockIdx < 0 || unlockIdx >= created.length) continue;

          const unlockCount = Math.max(
            1,
            previewField<number>(
              previewRows[i],
              'unlockPrerequisiteCorrectCount',
              'UnlockPrerequisiteCorrectCount'
            ) ?? 1
          );

          await adminService.updateQuestion(
            created[i].id,
            buildUpdateForUnlockLink(created[i], created[unlockIdx].id, unlockCount)
          );
          unlockDone += 1;
          setImportProgress({ current: unlockDone, total: unlockTotal, phase: 'unlock' });
        }
      }

      setShowImportModal(false);
      setImportJson('');
      setImportPreview([]);
      await loadQuestions();
      void loadUnlockCatalog();
      setError(null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      setImportError(err.response?.data?.message || 'Error importing questions');
    } finally {
      setIsImporting(false);
      setImportProgress({ current: 0, total: 0, phase: '' });
    }
  };

  const importProgressPercent = useMemo(() => {
    if (!isImporting || importProgress.total === 0) return 0;
    const unlockTotal =
      quiz.characterUnlockEnabled ? countUnlockLinksInPreview(importPreview as Record<string, unknown>[]) : 0;
    const creatingWeight = unlockTotal > 0 ? 90 : 100;

    if (importProgress.phase === 'creating') {
      return Math.min(
        creatingWeight,
        Math.round((importProgress.current / importProgress.total) * creatingWeight)
      );
    }
    if (importProgress.phase === 'unlock') {
      return Math.min(
        100,
        90 + Math.round((importProgress.current / importProgress.total) * 10)
      );
    }
    return 0;
  }, [isImporting, importProgress, importPreview, quiz.characterUnlockEnabled]);

  const loadExampleJson = async () => {
    if (isImagePoolQuiz) {
      const exampleData = isTileMatch
        ? [
            { ImageAssetKey: '001' },
            { ImageAssetKey: '002' },
            { ImageAssetKey: '003', Text: 'Peça opcional' },
          ]
        : [
            {
              Text: '001',
              ImageAssetKey: '001',
              SoundAssetKey: '001',
              Answers: [],
            },
          ];
      setImportJson(JSON.stringify(exampleData, null, 2));
      setImportError(null);
      setImportPreview([]);
      return;
    }
    if (isWitc) {
      const row: Record<string, unknown> = {
        Text: 'Quem é este personagem?',
        TextEn: 'Who is this character?',
        ImageAssetKey: 'sample_character_01',
        Tags: ['exemplo'],
      };
      if (quizNeedsEasy(quiz)) {
        row.CorrectAnswerEasyPt = 'Nome do personagem';
        row.CorrectAnswerEasyEn = 'Character name';
      }
      if (quizNeedsMediumBand(quiz)) {
        row.CorrectAnswerMediumPt = 'Nome curto';
        row.CorrectAnswerMediumEn = 'Short name';
      }
      row.CorrectAnswerPt = 'Nome do personagem';
      row.CorrectAnswerEn = 'Character name';
      const exampleData = [row];
      setImportJson(JSON.stringify(exampleData, null, 2));
      setImportError(null);
      setImportPreview([]);
      return;
    }
    const baseAnswers = [
      { Text: 'Lisboa', TextEn: 'Lisbon' },
      { Text: 'Porto', TextEn: 'Porto' },
      { Text: 'Coimbra', TextEn: 'Coimbra' },
      { Text: 'Braga', TextEn: 'Braga' },
      { Text: 'Faro', TextEn: 'Faro' },
    ].map((a, i) => ({
      ...a,
      IsCorrect: i === 0,
      ...(quizNeedsEasy(quiz) ? { IsCorrectEasy: i === 0 } : {}),
      ...(quizNeedsMediumBand(quiz) ? { IsCorrectMedium: i === 0 } : {}),
    }));
    const exampleData = [
      {
        Text: 'Qual é a capital de Portugal?',
        TextEn: 'What is the capital of Portugal?',
        Answers: baseAnswers,
      },
    ];
    setImportJson(JSON.stringify(exampleData, null, 2));
    setImportError(null);
    setImportPreview([]);
  };

  const handleExportQuestions = async () => {
    let allQuestions: Question[] = [];
    try {
      let page = 1;
      let more = true;
      while (more) {
        const res = await adminService.getQuizQuestions(quiz.id, {
          page,
          pageSize: QUESTION_MANAGER_PAGE_SIZE,
        });
        allQuestions = allQuestions.concat(res.questions);
        more = res.hasNextPage;
        page += 1;
      }
    } catch {
      setError('Failed to load questions for export');
      return;
    }

    const exportData = allQuestions.map((question) => {
      if (isImagePoolQuiz) {
        const row: Record<string, unknown> = {
          ImageAssetKey: question.imageAssetKey,
          Answers: [],
        };
        const label = question.text?.trim();
        if (isGenius) {
          row.Text = label || question.imageAssetKey;
          if (question.textEn) row.TextEn = question.textEn;
          const snd = question.soundAssetKey?.trim();
          if (snd) row.SoundAssetKey = snd;
        } else if (label && label !== 'Item' && label !== question.imageAssetKey) {
          row.Text = label;
        }
        if (question.tags && question.tags.length > 0) row.Tags = question.tags;
        if (
          quiz.characterUnlockEnabled &&
          question.unlockPrerequisiteQuestionId
        ) {
          const prereqIdx = allQuestions.findIndex(
            (q) => q.id === question.unlockPrerequisiteQuestionId
          );
          if (prereqIdx >= 0) {
            row.UnlockPrerequisiteQuestionIndex = prereqIdx;
            row.UnlockPrerequisiteCorrectCount = Math.max(
              1,
              question.unlockPrerequisiteCorrectCount ?? 1
            );
          }
        }
        return row;
      }
      if (isWitc) {
        const row: Record<string, unknown> = {
          Text: question.text,
          ImageAssetKey: question.imageAssetKey,
        };
        if (question.textEn) row.TextEn = question.textEn;
        if (quizNeedsEasy(quiz)) {
          row.CorrectAnswerEasyPt = question.correctAnswerEasyPt ?? question.correctAnswerPt;
          row.CorrectAnswerEasyEn = question.correctAnswerEasyEn ?? question.correctAnswerEn;
        }
        if (quizNeedsMediumBand(quiz)) {
          row.CorrectAnswerMediumPt = question.correctAnswerMediumPt ?? question.correctAnswerPt;
          row.CorrectAnswerMediumEn = question.correctAnswerMediumEn ?? question.correctAnswerEn;
        }
        row.CorrectAnswerPt = question.correctAnswerPt;
        row.CorrectAnswerEn = question.correctAnswerEn;
        if (question.tags && question.tags.length > 0) row.Tags = question.tags;
        if (question.excludeFromEasyDifficulty) row.ExcludeFromEasyDifficulty = true;
        if (
          quiz.characterUnlockEnabled &&
          question.unlockPrerequisiteQuestionId
        ) {
          const prereqIdx = allQuestions.findIndex(
            (q) => q.id === question.unlockPrerequisiteQuestionId
          );
          if (prereqIdx >= 0) {
            row.UnlockPrerequisiteQuestionIndex = prereqIdx;
            row.UnlockPrerequisiteCorrectCount = Math.max(
              1,
              question.unlockPrerequisiteCorrectCount ?? 1
            );
          }
        }
        return row;
      }
      const row: Record<string, unknown> = {
        Text: question.text,
        TextEn: question.textEn,
        Answers: question.answers.map((answer) => {
          const row: Record<string, unknown> = {
            Text: answer.text,
            TextEn: answer.textEn ?? '',
          };
          if (answer.imageAssetKey?.trim()) row.ImageAssetKey = answer.imageAssetKey;
          if (quizNeedsEasy(quiz)) {
            row.IsCorrectEasy = answer.isCorrectEasy ?? answer.isCorrect;
            row.IsCorrect = row.IsCorrectEasy;
          }
          if (quizNeedsMediumBand(quiz)) {
            row.IsCorrectMedium = answer.isCorrectMedium ?? answer.isCorrect;
            if (!quizNeedsEasy(quiz)) row.IsCorrect = row.IsCorrectMedium;
            else if (!row.IsCorrect) row.IsCorrect = row.IsCorrectMedium;
          }
          if (!quizNeedsEasy(quiz) && !quizNeedsMediumBand(quiz)) {
            row.IsCorrect = answer.isCorrect;
          }
          return row;
        }),
      };
      if (question.imageAssetKey?.trim()) {
        row.ImageAssetKey = question.imageAssetKey;
      }
      if (question.tags && question.tags.length > 0) {
        row.Tags = question.tags;
      }
      if (question.excludeFromEasyDifficulty) row.ExcludeFromEasyDifficulty = true;
      if (
        quiz.characterUnlockEnabled &&
        question.unlockPrerequisiteQuestionId
      ) {
        const prereqIdx = allQuestions.findIndex(
          (q) => q.id === question.unlockPrerequisiteQuestionId
        );
        if (prereqIdx >= 0) {
          row.UnlockPrerequisiteQuestionIndex = prereqIdx;
          row.UnlockPrerequisiteCorrectCount = Math.max(
            1,
            question.unlockPrerequisiteCorrectCount ?? 1
          );
        }
      }
      return row;
    });

    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${quiz.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_questions.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const listRangeFrom =
    totalFiltered === 0 ? 0 : (listPage - 1) * QUESTION_MANAGER_PAGE_SIZE + 1;
  const listRangeTo = Math.min(listPage * QUESTION_MANAGER_PAGE_SIZE, totalFiltered);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-3">
        <div className="flex-grow-1 min-w-0 text-start">
          <div className="mb-2">
            <Button
              variant="outline-secondary"
              onClick={onBack}
              className="d-inline-flex align-items-center"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Quizzes
            </Button>
          </div>
          <h3 className="mb-0">
            Manage Questions: {getQuizDisplayTitle(quiz, i18n.language)}
          </h3>
          {quiz.status === 'Draft' &&
            !canActivateQuiz() &&
            quiz.activationBlockers &&
            quiz.activationBlockers.length > 0 && (
              <Alert variant="warning" className="mt-2 mb-0 small">
                {quiz.activationBlockers.join(' ')}
              </Alert>
            )}
        </div>
        <div className="flex-shrink-0">
          {canActivateQuiz() && (
            <Button variant="success" onClick={handleActivateQuiz} className="me-2">
              <i className="bi bi-play-circle me-2"></i>
              Activate Quiz
            </Button>
          )}
          <Button
            variant="info"
            onClick={() => setShowImportModal(true)}
            className="me-2"
          >
            <i className="bi bi-upload me-2"></i>
            Import JSON
          </Button>
          <Button 
            variant="outline-info" 
            onClick={handleExportQuestions} 
            className="me-2"
            disabled={(quiz.totalQuestions ?? 0) === 0}
          >
            <i className="bi bi-download me-2"></i>
            Export JSON
          </Button>
          <Button
            variant="outline-danger"
            onClick={() => setShowDeleteAllModal(true)}
            className="me-2"
            disabled={(quiz.totalQuestions ?? 0) === 0}
            title={t('admin.deleteAllQuestionsTitle', 'Remove every question and set quiz to draft')}
          >
            <i className="bi bi-trash3 me-2"></i>
            {t('admin.deleteAllQuestions', 'Delete all questions')}
          </Button>
          <Button variant="primary" onClick={() => {
            resetForm();
            setSelectedQuestion(null);
            setShowCreateModal(true);
          }}>
            <i className="bi bi-plus-circle me-2"></i>
            {isImagePoolQuiz
              ? t('admin.geniusAddItems', 'Adicionar itens')
              : t('admin.addQuestion', 'Add Question')}
          </Button>
        </div>
      </div>

      <div className="mb-3">
        <Card>
          <Card.Body>
            <Row>
              <Col md={3}>
                <strong>Difficulty:</strong> {quiz.difficulty}
              </Col>
              <Col md={3}>
                <strong>Quiz tags:</strong>{' '}
                {quiz.tags && quiz.tags.length > 0 ? (
                  <span className="small">{quiz.tags.join(', ')}</span>
                ) : (
                  <span className="text-muted small">—</span>
                )}
              </Col>
              <Col md={3}>
                <strong>Questions:</strong> {quiz.totalQuestions ?? 0} / {quiz.questionCount}
              </Col>
              <Col md={3}>
                <strong>Status:</strong> {quiz.status}
              </Col>
              <Col md={12} className="mt-2">
                <strong>Progress:</strong>
                <div className="progress mt-1">
                  <div
                    className="progress-bar"
                    style={{
                      width: `${Math.min(((quiz.totalQuestions ?? 0) / quiz.questionCount) * 100, 100)}%`,
                    }}
                  />
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (quiz.totalQuestions ?? 0) > 0 || listSearchApplied.trim() ? (
        <>
          <Row className="g-2 mb-3 align-items-end">
            <Col xs={12} md={6} lg={5}>
              <Form.Group className="mb-0">
                <Form.Label className="small text-muted mb-1">
                  {t('admin.quizListSearch', 'Search')}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    type="search"
                    value={listSearchInput}
                    onChange={(e) => setListSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyListSearch();
                      }
                    }}
                    placeholder={t(
                      'admin.questionListSearchPlaceholder',
                      'Filter by question text, tags, image key, answers…'
                    )}
                    autoComplete="off"
                    aria-describedby="question-list-search-hint"
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={applyListSearch}
                    title={t('admin.questionListSearchRun', 'Run search')}
                    aria-label={t('admin.questionListSearchRun', 'Run search')}
                  >
                    <i className="bi bi-search" aria-hidden />
                  </Button>
                </InputGroup>
                <Form.Text id="question-list-search-hint" className="text-muted">
                  {t(
                    'admin.questionListSearchSubmitHint',
                    'Press Enter or the search button to filter (search runs on the server).'
                  )}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col xs={12} md={6} lg={7} className="text-md-end pt-1">
              <span className="small text-muted">
                {totalFiltered === 0
                  ? t('admin.quizListShowingEmpty', 'No results')
                  : t('admin.quizListShowing', 'Showing {{from}}–{{to}} of {{total}}')
                      .replace('{{from}}', String(listRangeFrom))
                      .replace('{{to}}', String(listRangeTo))
                      .replace('{{total}}', String(totalFiltered))}
              </span>
            </Col>
          </Row>
          {totalFiltered === 0 ? (
            <p className="text-muted mb-0">
              {t('admin.questionListNoMatches', 'No questions match your search.')}
            </p>
          ) : (
            <>
              <Accordion>
                {questions.map((question, index) => {
                  const displayNum =
                    (listPage - 1) * QUESTION_MANAGER_PAGE_SIZE + index + 1;
                  return (
            <Accordion.Item key={question.id} eventKey={String(question.id)}>
              <Accordion.Header>
                <div className="d-flex justify-content-between align-items-center w-100 me-3 gap-2">
                  <span className="min-w-0 text-start flex-grow-1">
                    <div className="d-flex flex-wrap align-items-baseline gap-x-1">
                      <strong>{isImagePoolQuiz ? t('admin.geniusItem', 'Item') : 'Question'} {displayNum}:</strong>{' '}
                      {isImagePoolQuiz
                        ? truncateText(
                            question.text && question.text !== 'Item'
                              ? question.text
                              : question.imageAssetKey || '—',
                            40
                          )
                        : truncateText(question.text, isWitc || isStop ? 42 : 50)}
                      {isImagePoolQuiz ? (
                        <>
                          {' '}
                          <span className="text-muted">·</span>{' '}
                          <code className="small">{question.imageAssetKey}</code>
                          {isGenius ? (
                            <>
                              <span className="text-muted"> → </span>
                              <code className="small">
                                {question.soundAssetKey || question.imageAssetKey}
                              </code>
                            </>
                          ) : null}
                        </>
                      ) : isStop ? (
                        <>
                          {' '}
                          <span className="text-muted">·</span>{' '}
                          <span className="text-success fw-semibold">
                            {t('admin.questionRowAnswer', 'Answer')}:
                          </span>{' '}
                          <span className="fw-medium">
                            {truncateText(stopAnswerOneLine(question), 56)}
                          </span>
                        </>
                      ) : isWitc ? (
                        <>
                          {' '}
                          <span className="text-muted">·</span>{' '}
                          <span className="text-success fw-semibold">
                            {t('admin.questionRowAnswer', 'Answer')}:
                          </span>{' '}
                          <span className="fw-medium">
                            {truncateText(witcAnswerOneLine(question, quiz), 56)}
                          </span>
                        </>
                      ) : (
                        <>
                          {' '}
                          <span className="text-muted">·</span>{' '}
                          <span className="text-success fw-semibold">
                            {t('admin.questionRowAnswer', 'Answer')}:
                          </span>{' '}
                          <span className="fw-medium">
                            {truncateText(mcCorrectAnswerOneLine(question, quiz), 56)}
                          </span>
                        </>
                      )}
                    </div>
                    {quiz.characterUnlockEnabled && question.unlockPrerequisiteQuestionId ? (
                      <div className="small mt-1">
                        <i className="bi bi-unlock-fill text-primary me-1" aria-hidden />
                        <span className="fw-semibold">Pré-requisito:</span>{' '}
                        <span className="text-break">
                          {unlockPrerequisiteDisplayLabel(
                            question,
                            quiz,
                            isWitc,
                            isStop,
                            unlockById,
                            t('admin.unlockPrereqPreviewMissing', '(pergunta não encontrada)')
                          )}
                        </span>
                        {Math.max(1, question.unlockPrerequisiteCorrectCount ?? 1) > 1 ? (
                          <span className="text-muted">
                            {' '}
                            ({Math.max(1, question.unlockPrerequisiteCorrectCount ?? 1)}×)
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </span>
                  <div className="flex-shrink-0">
                    {isStop ? (
                      <Badge bg="primary" className="me-2">
                        Stop · {question.answers.length} respostas
                      </Badge>
                    ) : !isWitc ? (
                      <Badge bg="info" className="me-2">
                        {question.answers.filter((a) => a.isCorrect).length} correct
                        {question.imageAssetKey ? ` · img: ${question.imageAssetKey}` : ''}
                      </Badge>
                    ) : (
                      <Badge bg="secondary" className="me-2">
                        WITC {question.imageAssetKey ? `· ${question.imageAssetKey}` : ''}
                      </Badge>
                    )}
                    {question.excludeFromEasyDifficulty === true ? (
                      <Badge bg="warning" text="dark" className="me-2">
                        {t('admin.questionMediumHardOnlyBadge', 'Medium/hard only')}
                      </Badge>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClick(question);
                      }}
                      className="me-2"
                    >
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedQuestion(question);
                        setShowDeleteModal(true);
                      }}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <p><strong>Question:</strong> {question.text}</p>
                {quiz.characterUnlockEnabled && question.unlockPrerequisiteQuestionId ? (
                  <p className="small mb-2 border-start border-primary border-3 ps-2">
                    <strong>{t('admin.unlockAfterPrereq', 'Unlock')}:</strong>{' '}
                    {t(
                      'admin.unlockAfterPrereqDetail',
                      'Requires answering correctly «{{preview}}» {{count}} time(s) in graded play.'
                    )
                      .replace(
                        '{{preview}}',
                        unlockPrerequisiteDisplayLabel(
                          question,
                          quiz,
                          isWitc,
                          isStop,
                          unlockById,
                          t('admin.unlockPrereqPreviewMissing', '(unknown)')
                        )
                      )
                      .replace(
                        '{{count}}',
                        String(Math.max(1, question.unlockPrerequisiteCorrectCount ?? 1))
                      )}
                  </p>
                ) : null}
                <p className="small text-muted mb-2">
                  <strong>Extra tags:</strong>{' '}
                  {question.tags && question.tags.length > 0 ? question.tags.join(', ') : '—'}
                </p>
                {isWitc ? (
                  <div className="small">
                    <p>
                      <strong>EN:</strong> {question.textEn || '—'}
                    </p>
                    <p>
                      <strong>Imagem (chave):</strong> {question.imageAssetKey || '—'}
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="small">
                      <strong>EN:</strong> {question.textEn || '—'}
                    </p>
                    {question.imageAssetKey ? (
                      <p className="small mb-2">
                        <strong>Imagem (chave):</strong> {question.imageAssetKey}
                      </p>
                    ) : null}
                    <div className="row">
                      {question.answers.map((answer, answerIndex) => (
                        <div key={answer.id} className="col-md-6 mb-2">
                          <div className={`p-2 border rounded ${answer.isCorrect ? 'bg-success text-white' : 'bg-light'}`}>
                            <strong>{answerIndex + 1}.</strong> {answer.text}
                            <span className="d-block small mt-1 opacity-90">
                              EN: {answer.textEn || '—'}
                            </span>
                            {answer.isCorrect && <i className="bi bi-check-circle ms-2"></i>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Accordion.Body>
            </Accordion.Item>
                  );
                })}
              </Accordion>
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <Pagination className="mb-0 flex-wrap justify-content-center">
                    <Pagination.First
                      disabled={!listHasPrev}
                      onClick={() => setListPage(1)}
                    />
                    <Pagination.Prev
                      disabled={!listHasPrev}
                      onClick={() => setListPage((p) => Math.max(1, p - 1))}
                    />
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum =
                        Math.max(1, Math.min(totalPages - 4, listPage - 2)) + i;
                      if (pageNum > totalPages) return null;
                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === listPage}
                          onClick={() => setListPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      disabled={!listHasNext}
                      onClick={() => setListPage((p) => Math.min(totalPages, p + 1))}
                    />
                    <Pagination.Last
                      disabled={!listHasNext}
                      onClick={() => setListPage(totalPages)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <Card>
          <Card.Body className="text-center py-4">
            <p className="text-muted">No questions found. Add your first question to get started.</p>
          </Card.Body>
        </Card>
      )}

      {/* Create Question Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isTileMatch
              ? t('admin.tileMatchAddPoolTitle', 'Adicionar itens ao pool Junta-peças')
              : isGenius
                ? t('admin.geniusAddPoolTitle', 'Adicionar itens ao pool Genius')
                : t('admin.addQuestionTitle', 'Add New Question')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {isWordPoolQuiz && (
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.wordConnectBulkLabel', 'Palavras do pool (vírgula ou uma por linha) *')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={10}
                  value={wordConnectBulkText}
                  onChange={(e) => setWordConnectBulkText(e.target.value)}
                  placeholder="GOKU, VEGETA, GOHAN"
                  spellCheck={false}
                />
                <Form.Text className="text-muted">
                  {t(
                    'admin.wordConnectBulkHelp',
                    'Todas as palavras do lote devem poder formar um único crucigrama (letras em comum).'
                  )}
                </Form.Text>
              </Form.Group>
            )}
            {isImagePoolQuiz && renderImagePoolBulkCreateFields()}

            {!isStop && !isImagePoolQuiz && !isWordPoolQuiz && (
              <Form.Group className="mb-3">
                <Form.Label>{isWitc ? 'Texto da pergunta (português) *' : 'Question Text *'}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  placeholder={isWitc ? 'Ex.: Quem é este Pokémon?' : 'Enter your question here...'}
                  required
                />
              </Form.Group>
            )}

            {!isWitc && !isStop && !isImagePoolQuiz && !isWordPoolQuiz && (
              <Form.Group className="mb-3">
                <Form.Label>Question text (English) *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.textEn}
                  onChange={(e) => setFormData({ ...formData, textEn: e.target.value })}
                  placeholder="e.g. English wording for the same question"
                  required
                />
              </Form.Group>
            )}

            {!isImagePoolQuiz && !isWordPoolQuiz && (
              <Form.Group className="mb-3">
                <Form.Label>Extra tags (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="e.g. Legendary — comma-separated; quiz tags also apply"
                />
                <Form.Text className="text-muted">
                  Added on top of quiz-level tags for achievements.
                </Form.Text>
              </Form.Group>
            )}

            {!isWitc && !isStop && !isImagePoolQuiz && !isWordPoolQuiz && (
              <Form.Group className="mb-3">
                <Form.Label>Imagem da pergunta (opcional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.imageAssetKey}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      imageAssetKey: e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''),
                    })
                  }
                  placeholder="nome do ficheiro sem extensão (.webp ou .png)"
                />
                <Form.Text className="text-muted">
                  Pasta raiz do quiz: <code>public/assets/images/{quiz.mcImageAssetsBasePath?.trim() || '…'}/</code>
                  . Configure a pasta no quiz antes de usar imagens.
                </Form.Text>
              </Form.Group>
            )}

            {isStop ? (
              renderStopQuestionFields()
            ) : isWitc ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Question text (English) *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={witcFields.textEn}
                    onChange={(e) => setWitcFields({ ...witcFields, textEn: e.target.value })}
                    placeholder="e.g. Who is that Pokémon?"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Nome do ficheiro (sem extensão; .webp ou .png) *</Form.Label>
                  <Form.Control
                    type="text"
                    value={witcFields.imageAssetKey}
                    onChange={(e) =>
                      setWitcFields({ ...witcFields, imageAssetKey: e.target.value.replace(/[^a-zA-Z0-9_-]/g, '') })
                    }
                    placeholder="ex.: pikachu-01"
                  />
                  <Form.Text className="text-muted">
                    WebP ou PNG em <code>public/assets/images/{quiz.witcImageAssetsBasePath || '…'}/</code>
                  </Form.Text>
                </Form.Group>
                {quizAllowsMediumOnlyQuestions(quiz) && (
                  <Form.Check
                    type="checkbox"
                    id="witc-create-exclude-easy"
                    className="mb-3"
                    label={t(
                      'admin.questionExcludeEasyHelp',
                      'This question only appears in medium and hard play modes (easy-mode answers stay empty).'
                    )}
                    checked={witcFields.excludeFromEasyDifficulty}
                    onChange={(e) =>
                      setWitcFields({
                        ...witcFields,
                        excludeFromEasyDifficulty: e.target.checked,
                      })
                    }
                  />
                )}
                {quizNeedsEasy(quiz) && !witcFields.excludeFromEasyDifficulty && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Resposta correta — modo fácil (PT) *</Form.Label>
                      <Form.Control
                        type="text"
                        value={witcFields.correctAnswerEasyPt}
                        onChange={(e) =>
                          setWitcFields({ ...witcFields, correctAnswerEasyPt: e.target.value })
                        }
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Resposta correta — modo fácil (EN) *</Form.Label>
                      <Form.Control
                        type="text"
                        value={witcFields.correctAnswerEasyEn}
                        onChange={(e) =>
                          setWitcFields({ ...witcFields, correctAnswerEasyEn: e.target.value })
                        }
                      />
                    </Form.Group>
                  </>
                )}
                {quizNeedsMediumBand(quiz) && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Resposta correta — modo médio / difícil (PT) *</Form.Label>
                      <Form.Control
                        type="text"
                        value={witcFields.correctAnswerMediumPt}
                        onChange={(e) =>
                          setWitcFields({ ...witcFields, correctAnswerMediumPt: e.target.value })
                        }
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Resposta correta — modo médio / difícil (EN) *</Form.Label>
                      <Form.Control
                        type="text"
                        value={witcFields.correctAnswerMediumEn}
                        onChange={(e) =>
                          setWitcFields({ ...witcFields, correctAnswerMediumEn: e.target.value })
                        }
                      />
                    </Form.Group>
                  </>
                )}
              </>
            ) : isImagePoolQuiz || isWordPoolQuiz ? null : (
              <>
                {quizAllowsMediumOnlyQuestions(quiz) && (
                  <Form.Check
                    type="checkbox"
                    className="mb-2"
                    id={showEditModal ? 'mc-edit-exclude-easy' : 'mc-create-exclude-easy'}
                    label={t(
                      'admin.questionExcludeEasyHelp',
                      'This question only appears in medium and hard play modes (easy-mode answers stay empty).'
                    )}
                    checked={formData.excludeFromEasyDifficulty}
                    onChange={(e) =>
                      setFormData({ ...formData, excludeFromEasyDifficulty: e.target.checked })
                    }
                  />
                )}
                <Form.Label>
                  Answers * (PT/EN; marque a correta para cada modo activo neste quiz)
                </Form.Label>
                {formData.answers.map((answer, index) => (
                  <Form.Group key={index} className="mb-3">
                    <Row className="g-2 align-items-end">
                      <Col md={quizNeedsEasy(quiz) && quizNeedsMediumBand(quiz) ? 4 : 5}>
                        <Form.Label className="small text-muted mb-1">PT — opção {index + 1}</Form.Label>
                        <Form.Control
                          type="text"
                          value={answer.text}
                          onChange={(e) => handleAnswerChange(index, 'text', e.target.value)}
                          placeholder={`Resposta ${index + 1} (PT)`}
                          required
                        />
                      </Col>
                      <Col md={quizNeedsEasy(quiz) && quizNeedsMediumBand(quiz) ? 4 : 5}>
                        <Form.Label className="small text-muted mb-1">EN — option {index + 1}</Form.Label>
                        <Form.Control
                          type="text"
                          value={answer.textEn}
                          onChange={(e) => handleAnswerChange(index, 'textEn', e.target.value)}
                          placeholder={`Answer ${index + 1} (EN)`}
                          required
                        />
                      </Col>
                      {quizNeedsEasy(quiz) && !formData.excludeFromEasyDifficulty && (
                        <Col md={2}>
                          <Form.Check
                            type="radio"
                            name="correctAnswerEasy"
                            className="mb-2"
                            checked={answer.isCorrectEasy}
                            onChange={(e) =>
                              handleAnswerChange(index, 'isCorrectEasy', e.target.checked)
                            }
                            label="Fácil"
                          />
                        </Col>
                      )}
                      {quizNeedsMediumBand(quiz) && (
                        <Col md={2}>
                          <Form.Check
                            type="radio"
                            name="correctAnswerMedium"
                            className="mb-2"
                            checked={answer.isCorrectMedium}
                            onChange={(e) =>
                              handleAnswerChange(index, 'isCorrectMedium', e.target.checked)
                            }
                            label="Médio"
                          />
                        </Col>
                      )}
                      {quiz.mcUseImageAnswers && (
                        <Col xs={12}>
                          <Form.Label className="small text-muted mb-1">
                            Imagem da opção {index + 1} (opcional)
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={answer.imageAssetKey}
                            onChange={(e) =>
                              handleAnswerChange(
                                index,
                                'imageAssetKey',
                                e.target.value.replace(/[^a-zA-Z0-9_-]/g, '')
                              )
                            }
                            placeholder="nome do ficheiro sem extensão (.webp ou .png)"
                          />
                          <Form.Text className="text-muted">
                            <code>
                              public/assets/{quiz.mcAnswerImageAssetsBasePath?.trim() || '…'}/
                            </code>
                            — sem imagem, o jogador vê o texto.
                          </Form.Text>
                        </Col>
                      )}
                    </Row>
                  </Form.Group>
                ))}
              </>
            )}
            {!isImagePoolQuiz && renderCharacterUnlockSection()}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={geniusBulkSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateQuestion}
            disabled={!isFormValid() || geniusBulkSaving}
          >
            {geniusBulkSaving
              ? t('admin.geniusBulkSaving', 'Salvando…')
              : isImagePoolQuiz
                ? t('admin.geniusAddItems', 'Adicionar itens')
                : t('admin.addQuestion', 'Add Question')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Question Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isTileMatch
              ? t('admin.tileMatchEditPoolTitle', 'Editar item do pool Junta-peças')
              : isGenius
                ? t('admin.geniusEditPoolTitle', 'Editar item do pool Genius')
                : t('admin.editQuestionTitle', 'Edit Question')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {isImagePoolQuiz && renderImagePoolEditFields()}

            {!isStop && !isImagePoolQuiz && !isWordPoolQuiz && (
              <Form.Group className="mb-3">
                <Form.Label>{isWitc ? 'Texto da pergunta (português) *' : 'Question Text *'}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  placeholder={isWitc ? 'Ex.: Quem é este Pokémon?' : 'Enter your question here...'}
                  required
                />
              </Form.Group>
            )}

            {!isWitc && !isStop && !isImagePoolQuiz && !isWordPoolQuiz && (
              <Form.Group className="mb-3">
                <Form.Label>Question text (English) *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.textEn}
                  onChange={(e) => setFormData({ ...formData, textEn: e.target.value })}
                  placeholder="e.g. English wording for the same question"
                  required
                />
              </Form.Group>
            )}

            {!isImagePoolQuiz && !isWordPoolQuiz && (
              <Form.Group className="mb-3">
                <Form.Label>Extra tags (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="e.g. Legendary — clear field to remove extra tags only"
                />
                <Form.Text className="text-muted">
                  Save empty to clear question-only tags (quiz tags unchanged).
                </Form.Text>
              </Form.Group>
            )}

            {!isWitc && !isStop && !isImagePoolQuiz && !isWordPoolQuiz && (
              <Form.Group className="mb-3">
                <Form.Label>Imagem da pergunta (opcional)</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.imageAssetKey}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      imageAssetKey: e.target.value.replace(/[^a-zA-Z0-9_-]/g, ''),
                    })
                  }
                  placeholder="nome do ficheiro sem extensão"
                />
                <Form.Text className="text-muted">
                  <code>public/assets/images/{quiz.mcImageAssetsBasePath?.trim() || '…'}/</code>
                </Form.Text>
              </Form.Group>
            )}

            {isStop ? (
              renderStopQuestionFields()
            ) : isWordPoolQuiz ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Palavra (português) *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.text}
                    onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                    placeholder="Ex.: NARUTO"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Palavra (English) *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.textEn}
                    onChange={(e) => setFormData({ ...formData, textEn: e.target.value })}
                    placeholder="Ex.: NARUTO"
                    required
                  />
                </Form.Group>
              </>
            ) : isWitc ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Question text (English) *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={witcFields.textEn}
                    onChange={(e) => setWitcFields({ ...witcFields, textEn: e.target.value })}
                    placeholder="e.g. Who is that Pokémon?"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Nome do ficheiro (sem extensão; .webp ou .png) *</Form.Label>
                  <Form.Control
                    type="text"
                    value={witcFields.imageAssetKey}
                    onChange={(e) =>
                      setWitcFields({ ...witcFields, imageAssetKey: e.target.value.replace(/[^a-zA-Z0-9_-]/g, '') })
                    }
                  />
                </Form.Group>
                {quizAllowsMediumOnlyQuestions(quiz) && (
                  <Form.Check
                    type="checkbox"
                    id="witc-edit-exclude-easy"
                    className="mb-3"
                    label={t(
                      'admin.questionExcludeEasyHelp',
                      'This question only appears in medium and hard play modes (easy-mode answers stay empty).'
                    )}
                    checked={witcFields.excludeFromEasyDifficulty}
                    onChange={(e) =>
                      setWitcFields({
                        ...witcFields,
                        excludeFromEasyDifficulty: e.target.checked,
                      })
                    }
                  />
                )}
                {quizNeedsEasy(quiz) && !witcFields.excludeFromEasyDifficulty && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Resposta correta — modo fácil (PT) *</Form.Label>
                      <Form.Control
                        type="text"
                        value={witcFields.correctAnswerEasyPt}
                        onChange={(e) =>
                          setWitcFields({ ...witcFields, correctAnswerEasyPt: e.target.value })
                        }
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Resposta correta — modo fácil (EN) *</Form.Label>
                      <Form.Control
                        type="text"
                        value={witcFields.correctAnswerEasyEn}
                        onChange={(e) =>
                          setWitcFields({ ...witcFields, correctAnswerEasyEn: e.target.value })
                        }
                      />
                    </Form.Group>
                  </>
                )}
                {quizNeedsMediumBand(quiz) && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Resposta correta — modo médio / difícil (PT) *</Form.Label>
                      <Form.Control
                        type="text"
                        value={witcFields.correctAnswerMediumPt}
                        onChange={(e) =>
                          setWitcFields({ ...witcFields, correctAnswerMediumPt: e.target.value })
                        }
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Resposta correta — modo médio / difícil (EN) *</Form.Label>
                      <Form.Control
                        type="text"
                        value={witcFields.correctAnswerMediumEn}
                        onChange={(e) =>
                          setWitcFields({ ...witcFields, correctAnswerMediumEn: e.target.value })
                        }
                      />
                    </Form.Group>
                  </>
                )}
              </>
            ) : isImagePoolQuiz || isWordPoolQuiz ? null : (
              <>
                {quizAllowsMediumOnlyQuestions(quiz) && (
                  <Form.Check
                    type="checkbox"
                    className="mb-2"
                    id={showEditModal ? 'mc-edit-exclude-easy' : 'mc-create-exclude-easy'}
                    label={t(
                      'admin.questionExcludeEasyHelp',
                      'This question only appears in medium and hard play modes (easy-mode answers stay empty).'
                    )}
                    checked={formData.excludeFromEasyDifficulty}
                    onChange={(e) =>
                      setFormData({ ...formData, excludeFromEasyDifficulty: e.target.checked })
                    }
                  />
                )}
                <Form.Label>
                  Answers * (PT/EN; marque a correta para cada modo activo neste quiz)
                </Form.Label>
                {formData.answers.map((answer, index) => (
                  <Form.Group key={index} className="mb-3">
                    <Row className="g-2 align-items-end">
                      <Col md={quizNeedsEasy(quiz) && quizNeedsMediumBand(quiz) ? 4 : 5}>
                        <Form.Label className="small text-muted mb-1">PT — opção {index + 1}</Form.Label>
                        <Form.Control
                          type="text"
                          value={answer.text}
                          onChange={(e) => handleAnswerChange(index, 'text', e.target.value)}
                          placeholder={`Resposta ${index + 1} (PT)`}
                          required
                        />
                      </Col>
                      <Col md={quizNeedsEasy(quiz) && quizNeedsMediumBand(quiz) ? 4 : 5}>
                        <Form.Label className="small text-muted mb-1">EN — option {index + 1}</Form.Label>
                        <Form.Control
                          type="text"
                          value={answer.textEn}
                          onChange={(e) => handleAnswerChange(index, 'textEn', e.target.value)}
                          placeholder={`Answer ${index + 1} (EN)`}
                          required
                        />
                      </Col>
                      {quizNeedsEasy(quiz) && !formData.excludeFromEasyDifficulty && (
                        <Col md={2}>
                          <Form.Check
                            type="radio"
                            name="correctAnswerEasyEdit"
                            className="mb-2"
                            checked={answer.isCorrectEasy}
                            onChange={(e) =>
                              handleAnswerChange(index, 'isCorrectEasy', e.target.checked)
                            }
                            label="Fácil"
                          />
                        </Col>
                      )}
                      {quizNeedsMediumBand(quiz) && (
                        <Col md={2}>
                          <Form.Check
                            type="radio"
                            name="correctAnswerMediumEdit"
                            className="mb-2"
                            checked={answer.isCorrectMedium}
                            onChange={(e) =>
                              handleAnswerChange(index, 'isCorrectMedium', e.target.checked)
                            }
                            label="Médio"
                          />
                        </Col>
                      )}
                      {quiz.mcUseImageAnswers && (
                        <Col xs={12}>
                          <Form.Label className="small text-muted mb-1">
                            Imagem da opção {index + 1} (opcional)
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={answer.imageAssetKey}
                            onChange={(e) =>
                              handleAnswerChange(
                                index,
                                'imageAssetKey',
                                e.target.value.replace(/[^a-zA-Z0-9_-]/g, '')
                              )
                            }
                            placeholder="nome do ficheiro sem extensão (.webp ou .png)"
                          />
                          <Form.Text className="text-muted">
                            <code>
                              public/assets/{quiz.mcAnswerImageAssetsBasePath?.trim() || '…'}/
                            </code>
                            — sem imagem, o jogador vê o texto.
                          </Form.Text>
                        </Col>
                      )}
                    </Row>
                  </Form.Group>
                ))}
              </>
            )}
            {!isImagePoolQuiz && renderCharacterUnlockSection()}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleEditQuestion}
            disabled={!isFormValid()}
          >
            {isImagePoolQuiz
              ? t('admin.geniusSaveItem', 'Salvar item')
              : t('admin.updateQuestion', 'Update Question')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this question? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteQuestion}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteAllModal}
        onHide={() => !isPurgingAllQuestions && setShowDeleteAllModal(false)}
        backdrop={isPurgingAllQuestions ? 'static' : true}
        keyboard={!isPurgingAllQuestions}
      >
        <Modal.Header closeButton={!isPurgingAllQuestions}>
          <Modal.Title>
            {t('admin.deleteAllQuestionsModalTitle', 'Delete all questions')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            {i18n.t('admin.deleteAllQuestionsModalBody', {
              count: quiz.totalQuestions ?? 0,
              defaultValue:
                'This will permanently remove all {{count}} question(s) for this quiz. The quiz will be set to Draft and will need to be completed and activated again. This cannot be undone.',
            })}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteAllModal(false)}
            disabled={isPurgingAllQuestions}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAllQuestions}
            disabled={isPurgingAllQuestions}
          >
            {isPurgingAllQuestions
              ? i18n.t('admin.deleteAllQuestionsWorking', { defaultValue: 'Deleting…' })
              : t('admin.deleteAllQuestionsConfirm', 'Delete all')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import JSON Modal */}
      <Modal
        show={showImportModal}
        onHide={closeImportModal}
        size="xl"
        backdrop={isImporting ? 'static' : true}
        keyboard={!isImporting}
      >
        <Modal.Header closeButton={!isImporting}>
          <Modal.Title>Import Questions from JSON</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <h6>JSON Format:</h6>
            {isImagePoolQuiz ? (
              <>
                <p className="mb-2">
                  {isTileMatch
                    ? 'Array de itens do pool Junta-peças (Tile Match). Cada item deve ter:'
                    : 'Array de itens do pool Genius. Cada item deve ter:'}
                </p>
                <ul className="mb-0">
                  <li>
                    <strong>ImageAssetKey</strong> / imageAssetKey: chave do ficheiro (sem extensão) em{' '}
                    <code>
                      public/assets/images/
                      {isTileMatch
                        ? quiz.tileMatchImageAssetsBasePath || '…'
                        : quiz.geniusImageAssetsBasePath || '…'}
                      /
                    </code>
                  </li>
                  <li>
                    <strong>Text</strong> / text (opcional): rótulo no admin; se omitido, usa a chave da imagem
                  </li>
                  {isGenius ? (
                    <li>
                      <strong>SoundAssetKey</strong> / soundAssetKey (opcional): som em{' '}
                      <code>public/assets/sounds/{quiz.geniusSoundAssetsBasePath || '…'}/</code> — se omitido, usa a
                      mesma chave da imagem
                    </li>
                  ) : null}
                  <li>
                    <strong>Tags</strong> / tags (opcional)
                  </li>
                </ul>
                <p className="mb-0 mt-2">
                  Não use <strong>answers</strong>, <strong>TextEn</strong> nem respostas corretas. Propriedades
                  case-insensitive no servidor.
                </p>
              </>
            ) : isWitc ? (
              <>
                <p className="mb-2">Array de perguntas WITC (Who Is This Character). Cada item deve ter:</p>
                <ul className="mb-0">
                  <li>
                    <strong>Text</strong> / text: texto da pergunta (PT)
                  </li>
                  <li>
                    <strong>TextEn</strong> / textEn: texto da pergunta em inglês (obrigatório)
                  </li>
                  <li>
                    <strong>ImageAssetKey</strong> / imageAssetKey: nome base do ficheiro WebP ou PNG (sem extensão), na pasta de assets do quiz
                  </li>
                  <li>
                    <strong>CorrectAnswerEasyPt</strong>/<strong>CorrectAnswerEasyEn</strong> (modo fácil) e{' '}
                    <strong>CorrectAnswerMediumPt</strong>/<strong>CorrectAnswerMediumEn</strong> (médio — mesmo texto no modo difícil), conforme os modos ligados no quiz.{' '}
                    <strong>CorrectAnswerPt</strong>/<strong>CorrectAnswerEn</strong> (legado) também são aceites e preenchem ambos se os específicos faltarem.
                  </li>
                  <li>
                    <strong>Tags</strong> / tags (opcional): tags extra da pergunta
                  </li>
                </ul>
                <p className="mb-0 mt-2">Não use o campo <strong>answers</strong> em quizzes WITC. Os nomes das propriedades são case-insensitive no servidor.</p>
              </>
            ) : (
              <>
                <p className="mb-2">
                  Array de perguntas de múltipla escolha bilíngues. Cada item deve ter:
                </p>
                <ul className="mb-0">
                  <li>
                    <strong>Text</strong> / text: pergunta em português
                  </li>
                  <li>
                    <strong>TextEn</strong> / textEn: mesma pergunta em inglês (obrigatório)
                  </li>
                  <li>
                    <strong>Answers</strong> / answers: exatamente 5 respostas
                  </li>
                  <li>
                    <strong>ImageAssetKey</strong> / imageAssetKey (opcional): nome base em{' '}
                    <code>public/assets/images/&lt;sua-pasta&gt;/</code> — exige <code>mcImageAssetsBasePath</code> no cadastro
                    do quiz
                  </li>
                  <li>
                    <strong>Tags</strong> / tags (opcional): tags extra da pergunta
                  </li>
                </ul>
                <p className="mb-0 mt-2">
                  Cada resposta: <strong>Text</strong>, <strong>TextEn</strong>; opcionalmente <strong>ImageAssetKey</strong>{' '}
                  (imagem da opção em <code>public/assets/&lt;pasta-do-quiz&gt;/</code> quando &quot;Utilizar imagens como perguntas?&quot; está activo — sem chave, usa o texto).
                  Use <strong>IsCorrect</strong>, ou <strong>IsCorrectEasy</strong> / <strong>IsCorrectMedium</strong> quando o quiz tiver vários modos.
                </p>
                <p className="mb-0 mt-2">
                  <strong>Note:</strong> Property names are case-insensitive on the server.
                </p>
              </>
            )}
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>JSON Data</Form.Label>
            <div className="mb-2">
              <Button variant="outline-secondary" size="sm" onClick={loadExampleJson} className="me-2">
                <i className="bi bi-download me-1"></i>
                Load Example
              </Button>
              <Button variant="outline-danger" size="sm" onClick={() => {
                setImportJson('');
                setImportError(null);
                setImportPreview([]);
              }}>
                <i className="bi bi-trash me-1"></i>
                Clear
              </Button>
            </div>
            <Form.Control
              as="textarea"
              rows={10}
              value={importJson}
              disabled={isImporting}
              onChange={(e) => {
                setImportJson(e.target.value);
                // Clear error when user starts typing
                if (importError) {
                  setImportError(null);
                }
              }}
              placeholder={
                isImagePoolQuiz
                  ? isTileMatch
                    ? `[
  { "ImageAssetKey": "001" },
  { "ImageAssetKey": "002", "Text": "rótulo opcional" }
]`
                    : `[
  {
    "Text": "001",
    "ImageAssetKey": "001",
    "SoundAssetKey": "001",
    "Answers": []
  }
]`
                  : isWitc
                  ? `[
  {
    "Text": "Quem é este personagem?",
    "TextEn": "Who is this character?",
    "ImageAssetKey": "hero_01",
    "CorrectAnswerPt": "Nome correto",
    "CorrectAnswerEn": "Correct name",
    "Tags": ["opcional"]
  }
]`
                  : `[
  {
    "Text": "Qual é a capital de França?",
    "TextEn": "What is the capital of France?",
    "ImageAssetKey": "optional_map_image",
    "Tags": ["Kanto"],
    "Answers": [
      {"Text": "Londres", "TextEn": "London", "IsCorrect": false},
      {"Text": "Paris", "TextEn": "Paris", "IsCorrect": true},
      {"Text": "Berlim", "TextEn": "Berlin", "IsCorrect": false},
      {"Text": "Madrid", "TextEn": "Madrid", "IsCorrect": false},
      {"Text": "Roma", "TextEn": "Rome", "IsCorrect": false}
    ]
  }
]`
              }
            />
          </Form.Group>

          {importError && (
            <Alert variant="danger">
              <strong>Validation Error:</strong> {importError}
            </Alert>
          )}

          {isImporting && importProgress.total > 0 && (
            <div className="mb-3">
              <p className="small text-muted mb-2">
                {importProgress.phase === 'unlock'
                  ? t('admin.importJsonProgressUnlock', {
                      defaultValue:
                        'Aplicando pré-requisitos de desbloqueio ({{current}}/{{total}})…',
                      current: importProgress.current,
                      total: importProgress.total,
                    })
                  : t('admin.importJsonProgressCreating', {
                      defaultValue: 'Importando pergunta {{current}} de {{total}}…',
                      current: importProgress.current,
                      total: importProgress.total,
                    })}
              </p>
              <ProgressBar
                now={importProgressPercent}
                label={`${importProgressPercent}%`}
                striped
                animated
                variant="primary"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={importProgressPercent}
              />
            </div>
          )}

          {importPreview.length > 0 && (
            <div className="mb-3">
              <h6>Preview ({importPreview.length} questions):</h6>
              <div className="border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {importPreview.map((question: any, index: number) => (
                  <div key={index} className="mb-3">
                    <strong>{isImagePoolQuiz ? 'Item' : 'Question'} {index + 1}:</strong>{' '}
                    {question.text ?? question.Text}
                    {isImagePoolQuiz ? (
                      <div className="ms-3 mt-1 small">
                        <div>
                          <span className="text-muted">Image:</span>{' '}
                          <code>{question.imageAssetKey ?? question.ImageAssetKey}</code>
                        </div>
                        {isGenius && (question.soundAssetKey ?? question.SoundAssetKey) ? (
                          <div>
                            <span className="text-muted">Sound:</span>{' '}
                            <code>{question.soundAssetKey ?? question.SoundAssetKey}</code>
                          </div>
                        ) : null}
                      </div>
                    ) : isWitc ? (
                      <div className="ms-3 mt-1 small">
                        {(question.textEn ?? question.TextEn) && (
                          <div>
                            <span className="text-muted">EN:</span> {question.textEn ?? question.TextEn}
                          </div>
                        )}
                        <div>
                          <span className="text-muted">Image:</span> {question.imageAssetKey ?? question.ImageAssetKey}
                        </div>
                        {(question.correctAnswerEasyPt ?? question.CorrectAnswerEasyPt) != null && (
                          <div>
                            <span className="text-muted">Fácil PT:</span>{' '}
                            {question.correctAnswerEasyPt ?? question.CorrectAnswerEasyPt}{' '}
                            <span className="text-muted">| EN:</span>{' '}
                            {question.correctAnswerEasyEn ?? question.CorrectAnswerEasyEn}
                          </div>
                        )}
                        {(question.correctAnswerMediumPt ?? question.CorrectAnswerMediumPt) != null && (
                          <div>
                            <span className="text-muted">Médio PT:</span>{' '}
                            {question.correctAnswerMediumPt ?? question.CorrectAnswerMediumPt}{' '}
                            <span className="text-muted">| EN:</span>{' '}
                            {question.correctAnswerMediumEn ?? question.CorrectAnswerMediumEn}
                          </div>
                        )}
                        <div>
                          <span className="text-muted">Legado PT:</span>{' '}
                          {question.correctAnswerPt ?? question.CorrectAnswerPt}{' '}
                          <span className="text-muted">| EN:</span> {question.correctAnswerEn ?? question.CorrectAnswerEn}
                        </div>
                      </div>
                    ) : (
                      <div className="ms-3 mt-1 small">
                        {(question.textEn ?? question.TextEn) && (
                          <div className="mb-1">
                            <span className="text-muted">EN:</span> {question.textEn ?? question.TextEn}
                          </div>
                        )}
                        {(question.answers ?? question.Answers ?? []).map((answer: any, answerIndex: number) => (
                          <div
                            key={answerIndex}
                            className={`${
                              answer.isCorrect ?? answer.IsCorrect ? 'text-success fw-bold' : 'text-muted'
                            }`}
                          >
                            {answerIndex + 1}. {answer.text ?? answer.Text} · EN: {answer.textEn ?? answer.TextEn}{' '}
                            {(answer.isCorrectEasy ?? answer.IsCorrectEasy) && <span className="badge bg-info me-1">fácil</span>}
                            {(answer.isCorrectMedium ?? answer.IsCorrectMedium) && (
                              <span className="badge bg-secondary">médio</span>
                            )}
                            {(answer.isCorrect ?? answer.IsCorrect) &&
                              !(answer.isCorrectEasy ?? answer.IsCorrectEasy) &&
                              !(answer.isCorrectMedium ?? answer.IsCorrectMedium) &&
                              ' ✓'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeImportModal} disabled={isImporting}>
            Cancel
          </Button>
          <Button variant="outline-primary" onClick={handleImportPreview} disabled={isImporting}>
            Preview
          </Button>
          <Button
            variant="primary"
            onClick={handleImportQuestions}
            disabled={importPreview.length === 0 || isImporting}
          >
            {isImporting
              ? t('admin.importJsonImporting', 'Importando…')
              : t('admin.importJsonImportButton', {
                  defaultValue: 'Importar perguntas ({{count}})',
                  count: importPreview.length,
                })}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuestionManager; 
